// ─────────────────────────────────────────────────────────────────────────────
//  sportsApi.js  —  Torch App · Sports Data Layer
//  Odds:  The Odds API (unchanged)
//  Live scores / period / clock:  ESPN public scoreboard API (free, no key)
// ─────────────────────────────────────────────────────────────────────────────

const API_KEY  = process.env.REACT_APP_ODDS_API_KEY || '';
const BASE_URL = 'https://api.the-odds-api.com/v4';

// ─── CACHE ───────────────────────────────────────────────────────────────────
let _gamesCache      = null;
let _gamesCacheTime  = 0;
let _propsCache      = null;
let _propsCacheTime  = 0;
let _activeSports    = null;

// ─── TEAM LOGO CACHE ────────────────────────────────────────────────────────
// Stores team name (normalized) → logo URL. Populated from:
//   1. ESPN scoreboard responses (covers ~28 leagues automatically)
//   2. TheSportsDB search fallback (covers everything else globally)
const _logoCache = {};
const _logoPending = {}; // prevents duplicate fetches for the same team

const normLogoKey = (name) => (name || '').trim().toLowerCase();

// Tokenize a team name for match verification: lowercase, strip punctuation,
// drop common filler/suffix words that differ across data sources (FC, CF,
// AFC, club, the, de, of...). Returns a Set of meaningful tokens.
const LOGO_STOPWORDS = new Set([
  'fc', 'cf', 'afc', 'sc', 'ac', 'club', 'cd', 'cp', 'the', 'de', 'of',
  'city', 'united', 'town', 'football', 'soccer', 'team',
]);
const logoTokens = (name) =>
  new Set(
    (name || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')   // hyphens/dots/accents-stripped upstream
      .split(/\s+/)
      .filter(Boolean)
      .filter(w => !LOGO_STOPWORDS.has(w))
  );

// Does TheSportsDB's returned team plausibly match the team we searched for?
// We require at least one shared meaningful token (after dropping FC/CF/etc.).
// This rejects the API's no-match default (e.g. it returns "Arsenal" for an
// unresolved query like "Paris Saint-Germain") which shares NO token with the
// query, while still accepting legitimate variants ("Paris Saint-Germain" vs
// "Paris SG", "Inter Miami" vs "Inter Miami CF"). On a single-token query that
// got stop-worded away, we conservatively reject (can't verify → initials).
const logoNameMatches = (query, returned) => {
  if (!query || !returned) return false;
  const q = logoTokens(query);
  const r = logoTokens(returned);
  if (q.size === 0 || r.size === 0) return false;
  for (const tok of q) {
    if (r.has(tok)) return true;
    // Allow prefix containment for abbreviated forms ("germain" vs "germ...").
    for (const rt of r) {
      if (tok.length >= 4 && rt.length >= 4 && (tok.startsWith(rt) || rt.startsWith(tok))) return true;
    }
  }
  return false;
};

// Called from ESPN parsing to store logos as we discover them
const cacheTeamLogo = (teamName, logoUrl) => {
  if (!teamName || !logoUrl) return;
  _logoCache[normLogoKey(teamName)] = logoUrl;
};

// TheSportsDB fallback — free API, no key required
const fetchLogoFromSportsDB = async (teamName) => {
  const key = normLogoKey(teamName);
  if (_logoCache[key]) return _logoCache[key];
  if (_logoPending[key]) return _logoPending[key];
  
  _logoPending[key] = (async () => {
    try {
      const url = `https://www.thesportsdb.com/api/v1/json/3/searchteams.php?t=${encodeURIComponent(teamName)}`;
      const res = await fetch(url);
      if (!res.ok) return null;
      const data = await res.json();
      const team = data?.teams?.[0] || null;
      const badge = team?.strBadge || team?.strLogo || null;
      // ⚠️ MATCH VERIFICATION (Bug fix): TheSportsDB's searchteams endpoint
      // returns its closest/first team even on a non-match — notoriously
      // "Arsenal" — which produced the wrong crest (e.g. PSG vs Arsenal showing
      // two Arsenal logos). Only accept the badge if the returned canonical
      // name actually shares a token with what we searched for. Otherwise treat
      // as no-match so TeamLogo falls back to initials rather than a wrong crest.
      const returnedName = team?.strTeam || team?.strAlternate || '';
      if (badge && logoNameMatches(teamName, returnedName)) {
        _logoCache[key] = badge;
        return badge;
      }
      if (badge) {
        console.warn(`[Torch/Logo] Rejected TheSportsDB no-match for "${teamName}" → "${returnedName}" (would have shown wrong crest). Falling back to initials.`);
      }
      return null;
    } catch {
      return null;
    } finally {
      delete _logoPending[key];
    }
  })();
  
  return _logoPending[key];
};

// Main lookup — returns cached URL instantly, or triggers async fetch
export const getTeamLogoUrl = (teamName) => {
  if (!teamName) return null;
  const cached = _logoCache[normLogoKey(teamName)];
  if (cached) return cached;
  // Trigger async fetch in background — won't block render
  // Next render will find it in cache
  fetchLogoFromSportsDB(teamName);
  return null;
};
let _activeSportsAge = 0;

const CACHE_TTL         = 2  * 60 * 1000;
const SPORTS_CACHE_TTL  = 60 * 60 * 1000;
const STALE_GAME_CUTOFF = 3  * 60 * 60 * 1000;

let _liveOddsCache = {};
const LIVE_ODDS_TTL = 30 * 1000;

// ESPN score cache — keyed by sportKey. As of 2026-06 this uses a single
// short TTL for every fetch (see getEspnScores). The earlier adaptive policy
// keyed the TTL off the PREVIOUS fetch's live flag, which left a just-kicked-off
// game stuck behind the long idle TTL right when fast updates matter most.
//
// Policy now:
//   • Always re-fetch on a 20s cadence. ESPN's public scoreboard endpoint is
//     free with no rate limit and its source caps at ~30s freshness, so 20s is
//     the practical floor — tighter would just hit cache. There is no idle path
//     anymore; a freshly-live game can never be served stale.
//
// `hasLive` is still recorded on each cache entry for diagnostics/logging, but
// it no longer influences the TTL. ESPN_CACHE_TTL_IDLE is retained only for
// reference and is intentionally unused.
let _espnCache = {};
const ESPN_CACHE_TTL_LIVE = 20 * 1000;
const ESPN_CACHE_TTL_IDLE = 90 * 1000; // retained for reference; no longer used

// ESPN team records cache — keyed by espnPath (e.g. "basketball/nba"),
// stores a map of `teamNameNormalized → "W-L"` summary string. ESPN's
// standings endpoint returns ALL teams in a league, so we get records for
// every team in one call regardless of whether they're playing today.
// 5-minute TTL — records change infrequently enough that this is safe.
let _teamRecordsCache = {};
const TEAM_RECORDS_TTL = 5 * 60 * 1000;

export const clearCache = () => {
  _gamesCache = null;  _gamesCacheTime  = 0;
  _propsCache = null;  _propsCacheTime  = 0;
  _activeSports = null; _activeSportsAge = 0;
  _liveOddsCache = {};
  _espnCache = {};
  _teamRecordsCache = {};
  for (const k in _urlCache) delete _urlCache[k];
};

// ─── USAGE TRACKER ───────────────────────────────────────────────────────────
let _callCount = 0;
let _remaining = null;

export const checkApiUsage = async () => ({ calls: _callCount, remaining: _remaining, limit: 500 });
export const getApiStatus  = ()       => ({ hasKey: !!API_KEY, calls: _callCount, remaining: _remaining, usingLive: !!API_KEY });

// ─── SPORT DISPLAY LABELS ────────────────────────────────────────────────────
// Complete map of every Odds API sport key → Torch display label.
// Source: https://the-odds-api.com/sports-odds-data/sports-apis.html (March 2026)
const SPORT_LABEL = {
  // ── US Major Pro Leagues ──
  basketball_nba:                    'NBA',
  basketball_nba_preseason:          'NBA',
  basketball_nba_all_stars:          'NBA',
  basketball_nba_summer_league:      'NBA',
  basketball_nba_championship_winner:'NBA',
  basketball_wnba:                   'WNBA',
  americanfootball_nfl:              'NFL',
  americanfootball_nfl_preseason:    'NFL',
  americanfootball_nfl_super_bowl_winner: 'NFL',
  icehockey_nhl:                     'NHL',
  icehockey_nhl_preseason:           'NHL',
  icehockey_nhl_championship_winner: 'NHL',
  baseball_mlb:                      'MLB',
  baseball_mlb_preseason:            'MLB',
  baseball_mlb_world_series_winner:  'MLB',

  // ── US College ──
  americanfootball_ncaaf:            'NCAAF',
  americanfootball_ncaaf_championship_winner: 'NCAAF',
  basketball_ncaab:                  'NCAAB',
  basketball_ncaab_championship_winner: 'NCAAB',
  basketball_wncaab:                 'WNCAAB',

  // ── US Other Leagues ──
  americanfootball_cfl:              'CFL',
  americanfootball_ufl:              'UFL',
  baseball_ncaa:                     'NCAA BB',
  lacrosse_pll:                      'PLL',
  lacrosse_ncaa:                     'NCAA LAX',

  // ── International Baseball ──
  baseball_milb:                     'MiLB',
  baseball_npb:                      'NPB',
  baseball_kbo:                      'KBO',

  // ── International Basketball ──
  basketball_euroleague:             'Euroleague',
  basketball_nbl:                    'NBL',

  // ── International Hockey ──
  icehockey_ahl:                     'AHL',
  icehockey_liiga:                   'Liiga',
  icehockey_mestis:                  'Mestis',
  icehockey_sweden_hockey_league:    'SHL',
  icehockey_sweden_allsvenskan:      'HockeyAllsv',

  // ── Combat Sports ──
  mma_mixed_martial_arts:            'MMA',
  boxing_boxing:                     'Boxing',

  // ── Soccer — Top 5 European Leagues ──
  soccer_epl:                        'EPL',
  soccer_spain_la_liga:              'La Liga',
  soccer_italy_serie_a:              'Serie A',
  soccer_germany_bundesliga:         'Bundesliga',
  soccer_france_ligue_one:           'Ligue 1',

  // ── Soccer — European Competitions ──
  soccer_uefa_champs_league:         'UCL',
  soccer_uefa_champs_league_qualification: 'UCL Qual',
  soccer_uefa_champs_league_women:   'UWCL',
  soccer_uefa_europa_league:         'UEL',
  soccer_uefa_europa_conference_league: 'UECL',

  // ── Soccer — England Lower ──
  soccer_efl_champ:                  'Championship',
  soccer_england_league1:            'EFL L1',
  soccer_england_league2:            'EFL L2',
  soccer_england_efl_cup:            'EFL Cup',
  soccer_fa_cup:                     'FA Cup',

  // ── Soccer — Germany Lower + Cups ──
  soccer_germany_bundesliga2:        'Bundesliga 2',
  soccer_germany_liga3:              '3. Liga',
  soccer_germany_dfb_pokal:          'DFB-Pokal',
  soccer_germany_bundesliga_women:   'Frauen BL',

  // ── Soccer — Spain Lower + Cups ──
  soccer_spain_segunda_division:     'La Liga 2',
  soccer_spain_copa_del_rey:         'Copa del Rey',

  // ── Soccer — Italy Lower + Cups ──
  soccer_italy_serie_b:              'Serie B',
  soccer_italy_coppa_italia:         'Coppa Italia',

  // ── Soccer — France Lower + Cups ──
  soccer_france_ligue_two:           'Ligue 2',
  soccer_france_coupe_de_france:     'Coupe de Fr',

  // ── Soccer — Other European ──
  soccer_netherlands_eredivisie:     'Eredivisie',
  soccer_portugal_primeira_liga:     'Primeira',
  soccer_turkey_super_league:        'Turkey SL',
  soccer_spl:                        'Scotland',
  soccer_belgium_first_div:          'Belgium',
  soccer_switzerland_superleague:    'Swiss SL',
  soccer_denmark_superliga:          'Denmark',
  soccer_sweden_allsvenskan:         'Sweden',
  soccer_sweden_superettan:          'Superettan',
  soccer_norway_eliteserien:         'Norway',
  soccer_finland_veikkausliiga:      'Finland',
  soccer_greece_super_league:        'Greece',
  soccer_poland_ekstraklasa:         'Poland',
  soccer_austria_bundesliga:         'Austria',
  soccer_russia_premier_league:      'Russia PL',
  soccer_league_of_ireland:          'Ireland',
  soccer_saudi_arabia_pro_league:    'Saudi PL',

  // ── Soccer — Americas ──
  soccer_usa_mls:                    'MLS',
  soccer_usa_mls_winner:             'MLS',
  soccer_brazil_campeonato:          'Brasileirao',
  soccer_brazil_serie_b:             'Brazil B',
  soccer_mexico_ligamx:              'Liga MX',
  soccer_argentina_primera_division: 'Argentina',
  soccer_chile_campeonato:           'Chile',
  soccer_conmebol_copa_libertadores: 'Copa Lib',
  soccer_conmebol_copa_sudamericana: 'Copa Sud',
  soccer_concacaf_gold_cup:          'Gold Cup',
  soccer_concacaf_leagues_cup:       'Leagues Cup',

  // ── Soccer — Asia/Pacific ──
  soccer_japan_j_league:             'J League',
  soccer_korea_kleague1:             'K League',
  soccer_australia_aleague:          'A-League',
  soccer_china_superleague:          'China SL',

  // ── Soccer — International ──
  soccer_fifa_world_cup:             'World Cup',
  soccer_fifa_world_cup_winner:      'World Cup',
  soccer_fifa_world_cup_qualifiers_europe:        'WCQ Europe',
  soccer_fifa_world_cup_qualifiers_south_america: 'WCQ CONMEBOL',
  soccer_fifa_world_cup_womens:      'WWC',
  soccer_fifa_club_world_cup:        'Club WC',
  soccer_uefa_european_championship: 'Euro',
  soccer_uefa_euro_qualification:    'Euro Qual',
  soccer_conmebol_copa_america:      'Copa America',
  soccer_africa_cup_of_nations:      'AFCON',
  soccer_uefa_nations_league:        'Nations Lge',
  soccer_international_friendlies:   'Friendlies',

  // Legacy keys (may still work in some API responses)
  soccer_fifa_world_cup_qualifiers:  'WCQ',
  soccer_uefa_euro:                  'Euro',
  soccer_uefa_euro_qualifiers:       'Euro Qual',

  // ── Golf ──
  golf_pga_tour:                     'Golf',
  golf_masters_tournament:           'Masters',
  golf_masters_tournament_winner:    'Masters',
  golf_masters_tournament_top_5:     'Masters',
  golf_masters_tournament_top_10:    'Masters',
  golf_masters_tournament_top_20:    'Masters',
  golf_masters_tournament_make_cut:  'Masters',
  golf_masters_tournament_matchups:  'Masters',
  golf_pga_championship:             'Golf',
  golf_pga_championship_winner:      'Golf',
  golf_the_open_championship:        'Golf',
  golf_the_open_championship_winner: 'Golf',
  golf_us_open:                      'Golf',
  golf_us_open_winner:               'Golf',

  // ── Tennis — ATP Grand Slams ──
  tennis_atp_aus_open_singles:       'Tennis',
  tennis_atp_french_open:            'Tennis',
  tennis_atp_wimbledon:              'Tennis',
  tennis_atp_us_open:                'Tennis',

  // ── Tennis — ATP Masters / Major ──
  tennis_atp_indian_wells:           'Tennis',
  tennis_atp_miami_open:             'Tennis',
  tennis_atp_monte_carlo_masters:    'Tennis',
  tennis_atp_madrid_open:            'Tennis',
  tennis_atp_italian_open:           'Tennis',
  tennis_atp_canadian_open:          'Tennis',
  tennis_atp_cincinnati_open:        'Tennis',
  tennis_atp_shanghai_masters:       'Tennis',
  tennis_atp_paris_masters:          'Tennis',
  tennis_atp_china_open:             'Tennis',
  tennis_atp_dubai:                  'Tennis',
  tennis_atp_qatar_open:             'Tennis',

  // ── Tennis — WTA Grand Slams ──
  tennis_wta_aus_open_singles:       'Tennis',
  tennis_wta_french_open:            'Tennis',
  tennis_wta_wimbledon:              'Tennis',
  tennis_wta_us_open:                'Tennis',

  // ── Tennis — WTA Major ──
  tennis_wta_indian_wells:           'Tennis',
  tennis_wta_miami_open:             'Tennis',
  tennis_wta_madrid_open:            'Tennis',
  tennis_wta_italian_open:           'Tennis',
  tennis_wta_canadian_open:          'Tennis',
  tennis_wta_cincinnati_open:        'Tennis',
  tennis_wta_china_open:             'Tennis',
  tennis_wta_dubai:                  'Tennis',
  tennis_wta_qatar_open:             'Tennis',
  tennis_wta_wuhan_open:             'Tennis',

  // Legacy tennis keys (may still work)
  tennis_atp_rome:                   'Tennis',
  tennis_atp_madrid:                 'Tennis',
  tennis_atp_shanghai:               'Tennis',
  tennis_atp_basel:                   'Tennis',
  tennis_atp_vienna:                 'Tennis',
  tennis_atp_paris:                  'Tennis',
  tennis_atp_cincinnati:             'Tennis',
  tennis_atp_finals:                 'Tennis',
  tennis_wta_rome:                   'Tennis',
  tennis_wta_madrid:                 'Tennis',
  tennis_wta_cincinnati:             'Tennis',
  tennis_wta_finals:                 'Tennis',

  // ── Aussie Rules ──
  aussierules_afl:                   'AFL',

  // ── Rugby ──
  rugbyleague_nrl:                   'NRL',
  rugbyleague_nrl_state_of_origin:   'NRL Origin',
  rugbyunion_six_nations:            'Six Nations',
  rugbyunion_super_rugby:            'Super Rugby',
  rugbyunion_rugby_world_cup:        'Rugby WC',

  // ── Cricket ──
  cricket_test_match:                'Cricket',
  cricket_odi:                       'Cricket',
  cricket_international_t20:         'Cricket',
  cricket_t20i:                      'Cricket',  // legacy alias
  cricket_ipl:                       'IPL',
  cricket_big_bash:                  'Cricket',
  cricket_psl:                       'Cricket',
  cricket_the_hundred:               'Cricket',
  cricket_caribbean_premier_league:  'Cricket',
  cricket_t20_blast:                 'Cricket',
  cricket_t20_world_cup:             'Cricket',
  cricket_icc_world_cup:             'Cricket',
  cricket_icc_world_cup_womens:      'Cricket',
  cricket_icc_trophy:                'Cricket',
  cricket_asia_cup:                  'Cricket',

  // ── Motorsport ──
  motorsport_formula_one:            'F1',
  motorsport_nascar:                 'NASCAR',

  // ── Handball ──
  handball_germany_bundesliga:       'Handball',
};

// ─── TEAM ABBREVIATIONS ──────────────────────────────────────────────────────
const ABBREVS = {
  'Boston Celtics':'BOS','Miami Heat':'MIA','Los Angeles Lakers':'LAL',
  'Golden State Warriors':'GSW','Denver Nuggets':'DEN','Phoenix Suns':'PHX',
  'New York Knicks':'NYK','Milwaukee Bucks':'MIL','Philadelphia 76ers':'PHI',
  'Chicago Bulls':'CHI','Oklahoma City Thunder':'OKC','Memphis Grizzlies':'MEM',
  'Dallas Mavericks':'DAL','Houston Rockets':'HOU','Minnesota Timberwolves':'MIN',
  'Sacramento Kings':'SAC','Cleveland Cavaliers':'CLE','Orlando Magic':'ORL',
  'Indiana Pacers':'IND','Atlanta Hawks':'ATL','Toronto Raptors':'TOR',
  'Charlotte Hornets':'CHA','Washington Wizards':'WAS','Detroit Pistons':'DET',
  'New Orleans Pelicans':'NOP','San Antonio Spurs':'SAS',
  'Portland Trail Blazers':'POR','Utah Jazz':'UTA',
  'Los Angeles Clippers':'LAC','Brooklyn Nets':'BKN',
  'Kansas City Chiefs':'KC','Buffalo Bills':'BUF','Dallas Cowboys':'DAL',
  'Philadelphia Eagles':'PHI','San Francisco 49ers':'SF','Detroit Lions':'DET',
  'Baltimore Ravens':'BAL','Cincinnati Bengals':'CIN','New England Patriots':'NE',
  'Miami Dolphins':'MIA','New York Giants':'NYG','New York Jets':'NYJ',
  'Los Angeles Rams':'LAR','Seattle Seahawks':'SEA','Green Bay Packers':'GB',
  'Chicago Bears':'CHI','Minnesota Vikings':'MIN','Tampa Bay Buccaneers':'TB',
  'Atlanta Falcons':'ATL','New Orleans Saints':'NO','Carolina Panthers':'CAR',
  'Washington Commanders':'WAS','Arizona Cardinals':'ARI','Las Vegas Raiders':'LV',
  'Denver Broncos':'DEN','Los Angeles Chargers':'LAC','Jacksonville Jaguars':'JAX',
  'Tennessee Titans':'TEN','Houston Texans':'HOU','Indianapolis Colts':'IND',
  'Pittsburgh Steelers':'PIT','Cleveland Browns':'CLE',
  'Toronto Maple Leafs':'TOR','Montreal Canadiens':'MTL',
  'Colorado Avalanche':'COL','Vegas Golden Knights':'VGK',
  'New York Rangers':'NYR','Pittsburgh Penguins':'PIT',
  'Boston Bruins':'BOS','Florida Panthers':'FLA',
  'Edmonton Oilers':'EDM','Calgary Flames':'CGY',
  'Vancouver Canucks':'VAN','Winnipeg Jets':'WPG',
  'Tampa Bay Lightning':'TBL','Carolina Hurricanes':'CAR',
  'New York Islanders':'NYI','New Jersey Devils':'NJD',
  'Washington Capitals':'WSH','Philadelphia Flyers':'PHI',
  'Minnesota Wild':'MIN','Chicago Blackhawks':'CHI',
  'Dallas Stars':'DAL','St. Louis Blues':'STL',
  'Nashville Predators':'NSH','Anaheim Ducks':'ANA',
  'Los Angeles Kings':'LAK','Seattle Kraken':'SEA',
  'San Jose Sharks':'SJS','Ottawa Senators':'OTT',
  'Buffalo Sabres':'BUF','Detroit Red Wings':'DET',
  'Columbus Blue Jackets':'CBJ','Utah Hockey Club':'UTA',
  'Los Angeles Dodgers':'LAD','San Francisco Giants':'SF',
  'New York Yankees':'NYY','Boston Red Sox':'BOS',
  'Atlanta Braves':'ATL','Houston Astros':'HOU',
  'Chicago Cubs':'CHC','St. Louis Cardinals':'STL',
  'Philadelphia Phillies':'PHI','New York Mets':'NYM',
  'Toronto Blue Jays':'TOR','Baltimore Orioles':'BAL',
  'Minnesota Twins':'MIN','Cleveland Guardians':'CLE',
  'Chicago White Sox':'CWS','Detroit Tigers':'DET',
  'Seattle Mariners':'SEA','Texas Rangers':'TEX',
  'Oakland Athletics':'OAK','Sacramento Athletics':'OAK',
  'Los Angeles Angels':'LAA','Tampa Bay Rays':'TB',
  'Kansas City Royals':'KC','San Diego Padres':'SD',
  'Colorado Rockies':'COL','Arizona Diamondbacks':'ARI',
  'Cincinnati Reds':'CIN','Pittsburgh Pirates':'PIT',
  'Miami Marlins':'MIA','Milwaukee Brewers':'MIL',
  'Washington Nationals':'WSH',
  'Atlanta United FC':'ATL','Inter Miami CF':'MIA',
  'LA Galaxy':'LAG','Los Angeles FC':'LAFC',
  'New York City FC':'NYC','New York Red Bulls':'NYRB',
  'Seattle Sounders FC':'SEA','Portland Timbers':'POR',
  'Toronto FC':'TOR','CF Montreal':'MTL','Columbus Crew':'CLB',
  'FC Cincinnati':'CIN','Nashville SC':'NSH','Charlotte FC':'CLT',
  'Austin FC':'ATX','St. Louis City SC':'STL',
  'San Jose Earthquakes':'SJ','Colorado Rapids':'COL',
  'Real Salt Lake':'RSL','Minnesota United':'MIN',
  'Chicago Fire FC':'CHI','D.C. United':'DC',
  'New England Revolution':'NE','Orlando City SC':'ORL',
  'Philadelphia Union':'PHI','FC Dallas':'DAL',
  'Houston Dynamo FC':'HOU','Sporting Kansas City':'SKC',
  'Vancouver Whitecaps FC':'VAN',
  'Arsenal':'ARS','Chelsea':'CHE','Liverpool':'LIV',
  'Manchester City':'MCI','Manchester United':'MUN',
  'Tottenham Hotspur':'TOT','Newcastle United':'NEW',
  'Aston Villa':'AVL','West Ham United':'WHU',
  'Brighton & Hove Albion':'BHA','Wolverhampton Wanderers':'WOL',
  'Fulham':'FUL','Brentford':'BRE','Crystal Palace':'CRY',
  'Everton':'EVE','Nottingham Forest':'NFO','Leicester City':'LEI',
  'Ipswich Town':'IPS','Southampton':'SOU','Bournemouth':'BOU',
  'Real Madrid':'RMA','FC Barcelona':'BAR','Atletico Madrid':'ATM',
  'Sevilla FC':'SEV','Real Betis':'BET','Real Sociedad':'RSO',
  'Villarreal CF':'VIL','Athletic Club':'ATH','Valencia CF':'VAL',
  'Bayern Munich':'BAY','Borussia Dortmund':'BVB','Paris Saint-Germain':'PSG',
  'Inter Milan':'INT','AC Milan':'MIL','Juventus':'JUV',
  'Ajax':'AJX','Benfica':'BEN','Porto':'POR','Sporting CP':'SCP',
};

const abbreviate = (name) => {
  if (ABBREVS[name]) return ABBREVS[name];
  const words = name.split(' ').filter(w => w.length > 2);
  if (words.length >= 2) return (words[0].slice(0, 3) + words[1][0]).toUpperCase();
  return name.slice(0, 4).toUpperCase();
};

// ─── ESPN LIVE SCORE LAYER ────────────────────────────────────────────────────
const ESPN_SPORT_PATH = {
  // US Major Leagues
  basketball_nba:             'basketball/nba',
  basketball_nba_preseason:   'basketball/nba',
  basketball_nba_summer_league:'basketball/nba',
  basketball_wnba:            'basketball/wnba',
  basketball_ncaab:           'basketball/mens-college-basketball',
  basketball_wncaab:          'basketball/womens-college-basketball',
  basketball_nbl:             'basketball/nbl',
  americanfootball_nfl:       'football/nfl',
  americanfootball_nfl_preseason: 'football/nfl',
  americanfootball_ncaaf:     'football/college-football',
  americanfootball_cfl:       'football/cfl',
  icehockey_nhl:              'hockey/nhl',
  icehockey_nhl_preseason:    'hockey/nhl',
  icehockey_ahl:              'hockey/ahl',
  icehockey_sweden_hockey_league: 'hockey/shl',
  baseball_mlb:               'baseball/mlb',
  baseball_mlb_preseason:     'baseball/mlb',
  // Lacrosse
  lacrosse_pll:               'lacrosse/pll',
  // Soccer — Major leagues ESPN covers
  soccer_usa_mls:             'soccer/usa.1',
  soccer_epl:                 'soccer/eng.1',
  soccer_efl_champ:           'soccer/eng.2',
  soccer_england_league1:     'soccer/eng.3',
  soccer_england_league2:     'soccer/eng.4',
  soccer_fa_cup:              'soccer/eng.fa',
  soccer_england_efl_cup:     'soccer/eng.league_cup',
  soccer_uefa_champs_league:  'soccer/uefa.champions',
  soccer_uefa_champs_league_qualification: 'soccer/uefa.champions_qual',
  soccer_uefa_europa_league:  'soccer/uefa.europa',
  soccer_uefa_europa_conference_league: 'soccer/uefa.europa.conf',
  soccer_spain_la_liga:       'soccer/esp.1',
  soccer_spain_segunda_division: 'soccer/esp.2',
  soccer_spain_copa_del_rey:  'soccer/esp.copa_del_rey',
  soccer_italy_serie_a:       'soccer/ita.1',
  soccer_italy_serie_b:       'soccer/ita.2',
  soccer_italy_coppa_italia:  'soccer/ita.coppa_italia',
  soccer_germany_bundesliga:  'soccer/ger.1',
  soccer_germany_bundesliga2: 'soccer/ger.2',
  soccer_germany_dfb_pokal:   'soccer/ger.dfb_pokal',
  soccer_france_ligue_one:    'soccer/fra.1',
  soccer_france_ligue_two:    'soccer/fra.2',
  soccer_netherlands_eredivisie: 'soccer/ned.1',
  soccer_portugal_primeira_liga: 'soccer/por.1',
  soccer_belgium_first_div:   'soccer/bel.1',
  soccer_turkey_super_league: 'soccer/tur.1',
  soccer_brazil_campeonato:   'soccer/bra.1',
  soccer_brazil_serie_b:      'soccer/bra.2',
  soccer_mexico_ligamx:       'soccer/mex.1',
  soccer_argentina_primera_division: 'soccer/arg.1',
  soccer_australia_aleague:   'soccer/aus.1',
  soccer_japan_j_league:      'soccer/jpn.1',
  soccer_korea_kleague1:      'soccer/kor.1',
  soccer_spl:                 'soccer/sco.1',
  soccer_switzerland_superleague: 'soccer/sui.1',
  soccer_austria_bundesliga:  'soccer/aut.1',
  soccer_denmark_superliga:   'soccer/den.1',
  soccer_sweden_allsvenskan:  'soccer/swe.1',
  soccer_norway_eliteserien:  'soccer/nor.1',
  soccer_conmebol_copa_libertadores: 'soccer/conmebol.libertadores',
  soccer_fifa_world_cup:      'soccer/fifa.world',
  soccer_uefa_european_championship: 'soccer/uefa.euro',
  soccer_uefa_euro:           'soccer/uefa.euro',
  soccer_conmebol_copa_america: 'soccer/conmebol.america',
  soccer_concacaf_gold_cup:   'soccer/concacaf.gold',
  soccer_concacaf_leagues_cup:'soccer/concacaf.leagues.cup',
  soccer_fifa_club_world_cup: 'soccer/fifa.cwc',
  soccer_uefa_nations_league: 'soccer/uefa.nations',
  soccer_saudi_arabia_pro_league: 'soccer/sau.1',
  // Aussie Rules + Rugby
  aussierules_afl:            'australian-football/afl',
  rugbyleague_nrl:            'rugby-league/nrl',
  // Cricket
  cricket_ipl:                'cricket/ipl',
  cricket_big_bash:           'cricket/big-bash',
  cricket_t20_world_cup:      'cricket/t20-world-cup',
  // MMA (ESPN covers UFC events)
  mma_mixed_martial_arts:     'mma/ufc',
};

const normTeam = (name = '') =>
  name.toLowerCase().replace(/[^a-z0-9 ]/g, '').trim().split(/\s+/).pop() || '';

const fetchEspnSport = async (espnPath) => {
  try {
    // LIVE-SCORE FRESHNESS (2026-07): this fetch runs on every live-score poll
    // (~15s while a game is live), so payload size directly drives perceived
    // score lag. It previously pulled a 7-DAY window (today..today+7) so that
    // upcoming playoff games would show their series state ("leads 2-0") before
    // being played. But for a busy sport that's ~100-200 events parsed every
    // 15s — a large JSON fetch + flatMap on the hot path — which is a major
    // source of the score latency users feel when making live predictions.
    //
    // We now fetch a TIGHT 3-day window: yesterday..tomorrow. This keeps:
    //   • all LIVE and today's games (with their own series state, which rides
    //     on each event object — live games are always in-window),
    //   • just-completed games (yesterday, for end-of-game settlement display),
    //   • timezone-edge games (a late game indexed under the adjacent UTC day).
    // The only thing dropped is series-state DISPLAY for games 2-7 days out,
    // which is nice-to-have enrichment on far-future playoff cards — an
    // acceptable trade for roughly-realtime live scores. fetchEspnSport has a
    // single caller (getEspnScores → the live-merge path); the settlement/
    // series-lookup path builds its own URLs server-side and is unaffected.
    const ymd = (d) => {
      const yy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yy}${mm}${dd}`;
    };
    const past = new Date();
    past.setDate(past.getDate() - 1);
    const future = new Date();
    future.setDate(future.getDate() + 1);
    const url = `https://site.api.espn.com/apis/site/v2/sports/${espnPath}/scoreboard?dates=${ymd(past)}-${ymd(future)}&limit=200`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    const events = data?.events || [];

    const parsed = events.flatMap(ev => {
      const comp   = ev.competitions?.[0];
      const status = ev.status || {};
      const stType = status.type || {};

      const home = comp?.competitors?.find(c => c.homeAway === 'home');
      const away = comp?.competitors?.find(c => c.homeAway === 'away');
      if (!home || !away) return [];

      const homeTeam  = home.team?.displayName || '';
      const awayTeam  = away.team?.displayName || '';
      // ESPN exposes two IDs per competitor:
      //   - `competitor.id`     → game-specific participant ID (changes per game)
      //   - `competitor.team.id` → team franchise ID (stable across games)
      // `series.competitors[].id` uses the FRANCHISE id, so we must match
      // against team.id first. Falling back to participant id is just defensive.
      const homeId    = home.team?.id || home.id || null;
      const awayId    = away.team?.id || away.id || null;
      // FIX C1/C3: preserve null scores — don't coerce to 0
      // parseInt(null) = NaN, we want null so downstream null guards work correctly
      const parseEspnScore = (val) => {
        if (val == null) return null;
        const n = parseInt(val, 10);
        return isNaN(n) ? null : n;
      };
      const homeScore = parseEspnScore(home.score);
      const awayScore = parseEspnScore(away.score);

      const inProgress = stType.description === 'In Progress' ||
                         stType.name === 'STATUS_IN_PROGRESS' ||
                         stType.state === 'in';
      const completed  = stType.completed === true ||
                         stType.description === 'Final' ||
                         stType.name === 'STATUS_FINAL';

      const periodNum = status.period ?? null;
      const rawClock  = status.displayClock || null;
      const clock     = rawClock && rawClock !== '0:00' ? rawClock : null;

      const isHalftime     = /halftime|half.?time/i.test(stType.description || '') ||
                             /STATUS_HALFTIME/i.test(stType.name || '');
      const isIntermission = /intermission/i.test(stType.description || '') ||
                             /STATUS_INTERMISSION/i.test(stType.name || '');
      const isEndOfPeriod  = /end.*period|end.*quarter/i.test(stType.description || '') ||
                             /STATUS_END_OF_PERIOD/i.test(stType.name || '');

      const isMlb = espnPath.includes('baseball');
      let periodLabel = null;
      if (isHalftime) {
        periodLabel = 'Halftime';
      } else if (isIntermission || isEndOfPeriod) {
        periodLabel = periodNum != null ? `End ${periodNum}` : 'Intermission';
      } else if (inProgress) {
        periodLabel = isMlb
          ? (stType.shortDetail || (periodNum != null ? String(periodNum) : null))
          : (periodNum != null ? String(periodNum) : null);
      }

      const _homeLogo = home.team?.logo || null;
      const _awayLogo = away.team?.logo || null;

      // ── Live in-game situation (free — same ESPN response) ──────────────
      // ESPN exposes `competitions[0].situation` while a game is in progress.
      // Shape varies by sport. We normalize the useful bits into `liveState`
      // so the UI can render a bases-diamond + count (MLB) or down & distance
      // (football) without any extra API calls. null when not live / absent.
      let liveState = null;
      if (inProgress && comp?.situation) {
        const sit = comp.situation;
        if (isMlb) {
          // Baseball: balls/strikes/outs + runners on base.
          liveState = {
            kind:    'baseball',
            balls:   sit.balls   ?? null,
            strikes: sit.strikes ?? null,
            outs:    sit.outs    ?? null,
            onFirst:  !!sit.onFirst,
            onSecond: !!sit.onSecond,
            onThird:  !!sit.onThird,
          };
        } else if (espnPath.includes('football')) {
          // Football: down & distance (e.g. "3rd & 7"), possession, field pos.
          liveState = {
            kind:        'football',
            down:        sit.down        ?? null,
            distance:    sit.distance    ?? null,
            downDistance: sit.downDistanceText || sit.shortDownDistanceText || null,
            possession:  sit.possession  ?? null,  // team id with the ball
            isRedZone:   !!sit.isRedZone,
          };
        } else if (espnPath.includes('basketball') || espnPath.includes('hockey')) {
          // Basketball/hockey have no discrete situation worth a diagram; the
          // period+clock already carries the live state. Leave liveState null
          // so the header falls back to the standard period label.
          liveState = null;
        }
      }

      // Cache these logos so they're available for future games with same teams
      if (_homeLogo) cacheTeamLogo(homeTeam, _homeLogo);
      if (_awayLogo) cacheTeamLogo(awayTeam, _awayLogo);

      // ── Probable pitcher (MLB) + team record (all sports) ──
      // ESPN exposes `competitors[].probables[]` for upcoming MLB games and
      // `competitors[].records[]` for season W-L. Both are optional and
      // sometimes empty (e.g. early-morning data, postponed games), so we
      // defensively read multiple field names.
      const parseProbablePitcher = (c) => {
        const p = c?.probables?.[0];
        if (!p) return null;
        const name = p.athlete?.displayName
                  || p.athlete?.fullName
                  || p.athlete?.shortName
                  || p.displayName
                  || p.name
                  || null;
        if (!name) return null;
        const stats = p.statistics || p.athlete?.statistics || [];
        const eraEntry = Array.isArray(stats) ? stats.find(s =>
          /^era$/i.test(s?.name || '') ||
          /^era$/i.test(s?.abbreviation || '') ||
          /^era$/i.test(s?.shortDisplayName || '')
        ) : null;
        const era = eraEntry?.displayValue || eraEntry?.value || null;
        return { name, era };
      };
      const parseRecord = (c) => {
        // ESPN exposes records on FIVE different paths depending on sport,
        // game state (live vs upcoming vs final), and which scoreboard endpoint
        // returned the data. We try each path and collect every record-like
        // value, then pick the most "total"-shaped one.
        //
        // The previous implementation only checked two paths (c.records,
        // c.team.record.items) and returned null for the other three —
        // which silently broke records on a chunk of games.
        const candidates = [];
        // Path 1: comp.records[] — most common, used by NBA/NHL/MLB
        if (Array.isArray(c?.records))             candidates.push(...c.records);
        // Path 2: comp.team.record.items[] — older shape, sometimes still present
        if (Array.isArray(c?.team?.record?.items)) candidates.push(...c.team.record.items);
        // Path 3: comp.team.records[] — newer shape on certain endpoints
        if (Array.isArray(c?.team?.records))       candidates.push(...c.team.records);
        // Path 4: comp.record (single string, rare but happens for live games)
        if (typeof c?.record === 'string' && c.record.trim()) {
          candidates.push({ summary: c.record, type: 'total' });
        }
        // Path 5: comp.team.record (string form)
        if (typeof c?.team?.record === 'string' && c.team.record.trim()) {
          candidates.push({ summary: c.team.record, type: 'total' });
        }
        if (candidates.length === 0) return null;

        // Prefer entries explicitly tagged as "total" or "overall". If none
        // match, return the first candidate's summary — even a sub-record
        // (home / road / vs-conf) is better than nothing because the user's
        // question is "how good is this team" and any record summary answers
        // that approximately. Won't show "12-3 home" though — only "summary"
        // strings that look like records (have a digit and a separator).
        const isTotalish = (r) =>
          /^total$/i.test(r?.type || '') ||
          /^total$/i.test(r?.name || '') ||
          /^overall$/i.test(r?.name || '') ||
          /^overall$/i.test(r?.type || '');
        const total = candidates.find(isTotalish) || candidates[0];
        const summary = total?.summary || total?.displayValue || null;
        // Validate it actually looks like a record (e.g. "65-72" or "65-72-1")
        // — defensive against summary fields containing other text.
        if (typeof summary !== 'string') return null;
        if (!/\d+[-–]\d+/.test(summary)) return null;
        return summary;
      };

      // ── Playoff series (NBA/NHL/WNBA/MLB/NFL) ─────────────────────────
      // ESPN scoreboard exposes `comp.series` for postseason games. Shape:
      //   { type: "playoff", title: "Eastern Conference - Semifinals",
      //     summary: "Detroit leads 2-0", status: "inprogress" | "post",
      //     competitors: [{ id, wins }, { id, wins }] }
      // We extract per-team wins (matched by team id), pick a tight round
      // label from the title, and pre-format human-readable status strings
      // ("Leads 2-0" / "Trails 0-2" / "Won 4-2"). Returns null when not a
      // playoff game OR when wins data is missing — the caller falls back
      // to the regular-season record line.
      const parseSeries = () => {
        // ESPN sometimes puts series at event-level instead of comp-level for
        // live games. Try both. Also `comp.series` is missing entirely on
        // some live game responses; in that case we may still get a usable
        // text in `comp.notes[]` or `comp.headlines[]` to infer round info.
        const s = comp?.series || ev?.series;

        // No series object at all → not a playoff game (or ESPN dropped it)
        if (!s) return null;

        // Filter out preseason/regular-season series objects. "playoff" is
        // the canonical type but some responses omit `type` entirely while
        // still being valid playoff data, so we only reject if explicitly
        // not-playoff.
        if (s.type && s.type !== 'playoff') return null;

        const homeC = (s.competitors || []).find(c => String(c?.id) === String(homeId));
        const awayC = (s.competitors || []).find(c => String(c?.id) === String(awayId));
        let homeWins = homeC?.wins ?? null;
        let awayWins = awayC?.wins ?? null;

        // Fallback: when ESPN drops series.competitors[].wins (sometimes
        // happens during live games — the `series` object is present but
        // the per-team wins counts are stripped), parse from the summary
        // string. ESPN's summary is consistently formatted as e.g.
        // "BUF leads series 1-0" / "MTL trails 0-1" / "DET wins series 4-2".
        // We match the team abbreviation to home/away and infer wins.
        if (homeWins == null || awayWins == null) {
          const summaryStr = String(s.summary || '');
          const m = summaryStr.match(/^([A-Z]{2,4})\s+(leads?|trails?|wins?|won|lost?)\s+(?:series\s+)?(\d+)\s*[-–]\s*(\d+)/i);
          if (m) {
            const [, abbr, verb, n1Str, n2Str] = m;
            const n1 = parseInt(n1Str, 10);
            const n2 = parseInt(n2Str, 10);
            const homeAbbr = (home.team?.abbreviation || '').toUpperCase();
            const awayAbbr = (away.team?.abbreviation || '').toUpperCase();
            const subjAbbr = abbr.toUpperCase();
            const isLost = /^(lost?|trails?)$/i.test(verb);
            // For "leads/wins/won": subject has n1, opponent has n2.
            // For "lost/trails":   subject has n1, opponent has n2 (always
            //   the score from subject's POV — "MTL lost 0-4" means MTL=0).
            const subjWins = isLost ? n1 : n1;
            const oppWins  = isLost ? n2 : n2;
            if (subjAbbr === homeAbbr) {
              homeWins = subjWins; awayWins = oppWins;
            } else if (subjAbbr === awayAbbr) {
              awayWins = subjWins; homeWins = oppWins;
            }
          }
        }

        if (homeWins == null || awayWins == null) return null;

        // ── Round label resolution ────────────────────────────────────
        // ESPN's `series.title` is unreliable: sometimes it's structured
        // ("Eastern Conference - Semifinals"), sometimes generic ("Playoff
        // Series"), sometimes empty. We try multiple sources in order of
        // specificity. If none match a known pattern, we DROP the round
        // label entirely rather than show a weak prefix that adds noise.
        //   1. series.title       (when structured)
        //   2. series.summary     (e.g. "Carolina leads 3-0 in Eastern Conf Semifinals")
        //   3. comp.notes[].headline
        //   4. comp.headlines[].description
        const isBball    = espnPath.includes('basketball');
        const isHockey   = espnPath.includes('hockey');
        const isBaseball = espnPath.includes('baseball');
        const isFootball = espnPath.includes('football') && !espnPath.includes('basketball');

        const matchRound = (raw) => {
          if (!raw) return null;
          // Strip leading conference/league prefix to expose the round name
          const stripped = String(raw).replace(
            /^.*?(eastern|western|american|national)\s+(conference|league)\s*[-–:]\s*/i, ''
          );
          const lc = stripped.toLowerCase();
          if (/\bfirst round\b|\bround 1\b|\b1st round\b|\bquarterfinals?\b/.test(lc)) return '1st Round';
          if (/\bsecond round\b|\bround 2\b|\b2nd round\b/.test(lc))                  return '2nd Round';
          if (isBball && /\bsemifinals?\b/.test(lc))                     return 'Conf Semis';
          if (isBball && /(conference|conf)\s*finals?/.test(lc))         return 'Conf Finals';
          if (isBball && /(nba|wnba)\s*finals?/.test(lc))                return 'Finals';
          if (isBball && /\bfinals?\b/.test(lc))                         return 'Finals';
          if (isHockey && /\bsemifinals?\b/.test(lc))                    return 'Conf Semis';
          if (isHockey && /(conference|conf)\s*finals?/.test(lc))        return 'Conf Finals';
          if (isHockey && /stanley cup/.test(lc))                        return 'Stanley Cup';
          if (isBaseball && /wild card/.test(lc))                        return 'Wild Card';
          if (isBaseball && /division series/.test(lc))                  return 'Division';
          if (isBaseball && /championship series/.test(lc))              return 'LCS';
          if (isBaseball && /world series/.test(lc))                     return 'World Series';
          if (isFootball && /wild card/.test(lc))                        return 'Wild Card';
          if (isFootball && /divisional/.test(lc))                       return 'Divisional';
          if (isFootball && /(conference|conf)\s*championship/.test(lc)) return 'Conf Champ';
          if (isFootball && /super bowl/.test(lc))                       return 'Super Bowl';
          return null;
        };

        const noteText = [
          ...(Array.isArray(comp?.notes) ? comp.notes : []),
          ...(Array.isArray(ev?.notes)   ? ev.notes   : []),
        ].map(n => n?.headline || n?.text || '').join(' ');
        const headlineText = Array.isArray(comp?.headlines)
          ? comp.headlines.map(h => h?.description || h?.shortLinkText || '').join(' ')
          : '';
        const round =
          matchRound(s.title)   ||
          matchRound(s.summary) ||
          matchRound(noteText)  ||
          matchRound(headlineText) ||
          null;
        // round can be null — render layer skips the prefix when missing.

        // Has the series ended? ESPN sets status="post" once decided.
        // Belt-and-suspenders: also infer from typical clinching counts.
        // Conservative: only count >=4 to avoid mis-flagging MLB Division
        // series (best-of-5) mid-run.
        const isOver = s.status === 'post' || homeWins >= 4 || awayWins >= 4;

        const fmtStatus = (mine, theirs) => {
          if (isOver) {
            if (mine > theirs) return `Won ${mine}-${theirs}`;
            if (mine < theirs) return `Lost ${theirs}-${mine}`;
            return `Tied ${mine}-${theirs}`;
          }
          if (mine > theirs) return `Leads ${mine}-${theirs}`;
          if (mine < theirs) return `Trails ${mine}-${theirs}`;
          return `Tied ${mine}-${theirs}`;
        };

        return {
          round, // may be null — render layer handles that case
          homeWins, awayWins,
          homeStatus: fmtStatus(homeWins, awayWins),
          awayStatus: fmtStatus(awayWins, homeWins),
          isOver,
        };
      };

      return [{
        homeTeamFull: homeTeam,
        awayTeamFull: awayTeam,
        homeTeamNorm: normTeam(homeTeam),
        awayTeamNorm: normTeam(awayTeam),
        // ── SERIES FIX, PRODUCER HALF (2026-07-26, round 7) ──────────────────
        // matchEspn's date-aware selection reads `candidate.commenceTime` and
        // SKIPS any candidate where it resolves to null. This field was never
        // emitted here, so every candidate was skipped and matchEspn returned
        // null for every caller that supplied a date — which is BOTH callers
        // (fetchLiveScoresForPicks passes pick.commenceTime; the games board
        // passes normalizeGame's `time`). Net effect of round 6 without this
        // line: no ESPN live scores, periods, clocks or liveState anywhere.
        //
        // It also feeds the legacy priority scorer's time-proximity term
        // (`500_000 - |now - commenceTime|`). Without it every completed
        // candidate scored identically and the strict `>` comparison left
        // insertion order deciding the winner — so the dateless path had no
        // series protection either.
        //
        // ESPN puts the scheduled start on the event (`ev.date`, ISO 8601);
        // `comp.date` is the same value and is the defensive fallback.
        commenceTime: ev.date || comp?.date || null,
        // Abbreviation lets matchEspn cross-reference when one source uses
        // "LA Lakers" and the other "Los Angeles Lakers". Both end up matchable
        // on the abbreviation "lal".
        homeTeamAbbr: home.team?.abbreviation || '',
        awayTeamAbbr: away.team?.abbreviation || '',
        homeLogo: _homeLogo,
        awayLogo: _awayLogo,
        homeScore,
        awayScore,
        inProgress,
        completed,
        period: periodLabel,
        clock:  inProgress ? clock : null,
        liveState,
        homePitcher: parseProbablePitcher(home),
        awayPitcher: parseProbablePitcher(away),
        homeRecord:  parseRecord(home),
        awayRecord:  parseRecord(away),
        playoff:     parseSeries(),
      }];
    });

    // ── Cross-game playoff series backfill ────────────────────────────
    // ESPN sometimes returns the `series` object on some games in a playoff
    // series and not others (no clear pattern — looks like timing of how
    // their CMS attached the data). Since the same two teams play 4-7 times
    // per series with the same series state visible on every game, we can
    // recover by caching series data from any game that has it, then
    // backfilling siblings keyed by the unordered team pair. Once any
    // game in a series surfaces parseable data, every game inherits it.
    const seriesByPair = new Map();
    const pairKey = (a, b) => a < b ? `${a}|${b}` : `${b}|${a}`;
    for (const g of parsed) {
      if (!g.playoff || !g.homeTeamFull || !g.awayTeamFull) continue;
      const k = pairKey(g.homeTeamFull, g.awayTeamFull);
      // Cache a normalized form (per-team wins) — orientation-agnostic so
      // it can be re-applied to any sibling game regardless of which team
      // is home/away in that game.
      seriesByPair.set(k, {
        round:      g.playoff.round,
        isOver:     g.playoff.isOver,
        winsByTeam: {
          [g.homeTeamFull]: g.playoff.homeWins,
          [g.awayTeamFull]: g.playoff.awayWins,
        },
      });
    }
    if (seriesByPair.size > 0) {
      const fmtStatus = (mine, theirs, isOver) => {
        if (isOver) {
          if (mine > theirs) return `Won ${mine}-${theirs}`;
          if (mine < theirs) return `Lost ${theirs}-${mine}`;
          return `Tied ${mine}-${theirs}`;
        }
        if (mine > theirs) return `Leads ${mine}-${theirs}`;
        if (mine < theirs) return `Trails ${mine}-${theirs}`;
        return `Tied ${mine}-${theirs}`;
      };
      for (const g of parsed) {
        if (g.playoff) continue; // already has data
        if (!g.homeTeamFull || !g.awayTeamFull) continue;
        const k = pairKey(g.homeTeamFull, g.awayTeamFull);
        const cached = seriesByPair.get(k);
        if (!cached) continue;
        const homeWins = cached.winsByTeam[g.homeTeamFull];
        const awayWins = cached.winsByTeam[g.awayTeamFull];
        if (homeWins == null || awayWins == null) continue;
        g.playoff = {
          round:      cached.round,
          homeWins, awayWins,
          homeStatus: fmtStatus(homeWins, awayWins, cached.isOver),
          awayStatus: fmtStatus(awayWins, homeWins, cached.isOver),
          isOver:     cached.isOver,
        };
      }
      // Optional debug — count how many backfills happened (helps diagnose
      // if ESPN ever stops returning series data entirely).
      const backfilled = parsed.filter(g =>
        g.playoff && !seriesByPair.get(pairKey(g.homeTeamFull, g.awayTeamFull))
      ).length;
      if (backfilled > 0) {
        console.info(`[Torch/ESPN] ${espnPath} — backfilled playoff series data for ${backfilled} game(s)`);
      }
    }

    console.log(
      `[Torch/ESPN] ${espnPath} — ${parsed.length} games:`,
      parsed.map(g => `${g.awayTeamFull} @ ${g.homeTeamFull} | ` +
        `${g.inProgress ? 'LIVE' : g.completed ? 'Final' : 'Upcoming'} ` +
        `${g.period ?? ''} ${g.clock ?? ''} | ${g.awayScore}–${g.homeScore} | ` +
        `rec: ${g.awayRecord || '∅'} / ${g.homeRecord || '∅'} | ` +
        `playoff: ${g.playoff ? `${g.playoff.round || '?'} ${g.playoff.awayStatus}/${g.playoff.homeStatus}` : '∅'}`)
    );
    return parsed;
  } catch (e) {
    console.warn(`[Torch/ESPN] Failed for ${espnPath}:`, e.message);
    return [];
  }
};

// ─── ESPN Team Records — standings endpoint ──────────────────────────────────
// Pulls ALL teams in a league with their record summary from ESPN's
// standings endpoint. Unlike the scoreboard (which only includes today's
// games), this gives us records for every team in the league — so we
// can show "65-72" or "82-80" etc. for any team in any game, including
// games where the scoreboard endpoint failed to match or didn't include
// the matchup at all.
//
// ESPN endpoint: /apis/v2/sports/{sport}/{league}/standings
// (Note: `/apis/v2/`, NOT `/apis/site/v2/` — different surface, same domain.)
const fetchTeamRecords = async (espnPath) => {
  // Reject the cached miss case too, so we don't keep retrying a broken
  // league silently. If the endpoint genuinely doesn't support standings
  // (some niche leagues), this returns {} once and caches that.
  const cached = _teamRecordsCache[espnPath];
  if (cached && Date.now() - cached.ts < TEAM_RECORDS_TTL) {
    return cached.records;
  }
  try {
    const url1 = `https://site.web.api.espn.com/apis/v2/sports/${espnPath}/standings`;
    const url2 = `https://site.api.espn.com/apis/v2/sports/${espnPath}/standings`;
    let res = null;
    let usedUrl = null;
    for (const u of [url1, url2]) {
      try {
        const r = await fetch(u);
        if (r.ok) { res = r; usedUrl = u; break; }
      } catch {}
    }
    if (!res) {
      console.warn(`[Torch/ESPN] Standings unreachable for ${espnPath}: tried both site.web.api + site.api`);
      _teamRecordsCache[espnPath] = { records: {}, ts: Date.now() };
      return {};
    }
    const data = await res.json();
    // The standings response shape varies slightly by sport. Walk all
    // possible paths to find team entries with stats. Common shapes:
    //   data.children[].standings.entries[].team{} + .stats[]
    //   data.standings.entries[].team{} + .stats[]
    //   data.entries[].team{} + .stats[]
    const allEntries = [];
    const harvest = (obj) => {
      if (!obj || typeof obj !== 'object') return;
      if (Array.isArray(obj.entries)) allEntries.push(...obj.entries);
      if (obj.standings) harvest(obj.standings);
      if (Array.isArray(obj.children)) obj.children.forEach(harvest);
      if (Array.isArray(obj.groups)) obj.groups.forEach(harvest);
    };
    harvest(data);

    // Build a multi-keyed map so we can look up by full name, abbreviation,
    // or last word — matching the strategy used in matchEspn.
    const records = {};
    const fullNorm = (s = '') => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    for (const entry of allEntries) {
      const team = entry.team || {};
      const stats = Array.isArray(entry.stats) ? entry.stats : [];
      // The "overall record" stat. ESPN names it inconsistently across
      // sports — try several. Prefer displayValue ("65-72") over numeric
      // values which would lose the format.
      let recordSummary = null;
      for (const s of stats) {
        const id = (s?.type || s?.name || s?.abbreviation || '').toLowerCase();
        if ((id === 'overall' || id === 'total' || id === 'record' || id === 'overall record') && s.displayValue) {
          recordSummary = s.displayValue;
          break;
        }
      }
      // Some sports expose record via team.record directly
      if (!recordSummary && team.record && typeof team.record === 'string') {
        recordSummary = team.record;
      }
      // Soccer fallback: ESPN soccer standings don't expose a single
      // "total" stat — they have separate wins/losses/ties/draws.
      // Build the record from those if available.
      if (!recordSummary) {
        const findStat = (matchers) => {
          for (const s of stats) {
            const id = (s?.type || s?.name || s?.abbreviation || '').toLowerCase();
            if (matchers.some(m => id === m)) {
              const v = s?.value ?? s?.displayValue;
              if (v != null && v !== '') return Number(v);
            }
          }
          return null;
        };
        const wins   = findStat(['wins', 'w']);
        const losses = findStat(['losses', 'l']);
        const draws  = findStat(['ties', 'draws', 'd', 't']);
        if (wins != null && losses != null) {
          recordSummary = draws != null
            ? `${wins}-${losses}-${draws}`
            : `${wins}-${losses}`;
        }
      }
      // Validate it looks like a record (has digits with separator)
      if (!recordSummary || !/\d+[-–]\d+/.test(recordSummary)) continue;

      // Register under every key we can derive from the team data
      const keys = [
        normTeam(team.displayName || ''),
        fullNorm(team.displayName || ''),
        fullNorm(team.shortDisplayName || ''),
        fullNorm(team.name || ''),
        fullNorm(team.abbreviation || ''),
      ].filter(Boolean);
      for (const k of keys) records[k] = recordSummary;
    }
    // Verbose log so we can see which teams + keys made it into the records map.
    // Knicks-style "missing team" cases will be obvious — the team name won't appear.
    const teamSummaries = allEntries.map(e => {
      const t = e.team || {};
      return `${t.displayName || t.name || '?'}=${(e.stats || []).find(s => (s?.type === 'total' || s?.name === 'overall'))?.displayValue || '∅'}`;
    });
    console.info(`[Torch/ESPN] standings ${espnPath}: ${Object.keys(records).length} keys for ${allEntries.length} teams`);
    console.info(`[Torch/ESPN] standings ${espnPath} teams:`, teamSummaries.join(' | '));
    _teamRecordsCache[espnPath] = { records, ts: Date.now() };
    return records;
  } catch (e) {
    console.warn(`[Torch/ESPN] standings ${espnPath} failed:`, e.message);
    _teamRecordsCache[espnPath] = { records: {}, ts: Date.now() };
    return {};
  }
};

// Look up a team's record across all relevant ESPN leagues. Tries multiple
// keys (last-word, full-name, abbreviation) since the Odds API and ESPN
// sometimes use different team naming conventions.
const lookupTeamRecord = (recordsMap, teamFull, teamAbbr) => {
  if (!recordsMap || typeof recordsMap !== 'object') return null;
  const fullNorm = (s = '') => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const keys = [
    normTeam(teamFull || ''),
    fullNorm(teamFull || ''),
    fullNorm(teamAbbr || ''),
  ].filter(Boolean);
  for (const k of keys) {
    if (recordsMap[k]) return recordsMap[k];
  }
  // Diagnostic: log the miss with what keys we tried so it's visible in console.
  // Helps spot "New York Knicks" lookups that fail but where "knicks" is keyed.
  if (teamFull && Object.keys(recordsMap).length > 0) {
    console.warn(`[Torch/ESPN] No record match for "${teamFull}" (abbr: ${teamAbbr || '∅'}). Tried keys: [${keys.join(', ')}]. Available samples: ${Object.keys(recordsMap).slice(0, 8).join(', ')}`);
  }
  return null;
};
const getEspnScores = async (sportKeys) => {
  const pathsNeeded = [...new Set(
    sportKeys
      .map(k => ESPN_SPORT_PATH[k])
      .filter(Boolean)
  )];

  // Fetch all ESPN sports in parallel (was sequential)
  const results = await Promise.allSettled(
    pathsNeeded.map(async (path) => {
      const cached = _espnCache[path];
      if (cached) {
        // FRESHNESS FIX (2026-06): the old adaptive TTL keyed off
        // `cached.hasLive`, which was computed on the PREVIOUS fetch. A game
        // that had just kicked off (not yet live last fetch) got stuck behind
        // the 90s idle TTL exactly when fast updates matter most. We now use a
        // single short TTL for every fetch regardless of the prior payload.
        // ESPN's public scoreboard endpoint is free with no rate limit, so the
        // only cost of a tighter window is negligible chatter; the benefit is
        // never serving a stale 0-0 for a game that went live since last fetch.
        if (Date.now() - cached.ts < ESPN_CACHE_TTL_LIVE) return cached.games;
      }
      const games = await fetchEspnSport(path);
      const hasLive = Array.isArray(games) && games.some(g => g?.inProgress);
      _espnCache[path] = { games, ts: Date.now(), hasLive };
      return games;
    })
  );

  const allGames = results
    .filter(r => r.status === 'fulfilled')
    .flatMap(r => r.value);

  // Multi-keyed lookup map. The previous design keyed only by `lastWord|lastWord`
  // (e.g. "phillies|rockies"), which broke when the Odds API and ESPN used
  // different team name formats. Now we register EVERY game under EVERY
  // pair of keys we can derive — last-word, full-name-normalized, and
  // Build all possible keys for home and away. With the 7-day forward fetch,
  // the same team-pair can appear in BOTH today's live game and an upcoming
  // game — e.g. Wild @ Colorado tonight (in progress) AND Wild @ Colorado
  // on Monday (scheduled). Without priority, the Monday game (registered
  // later) overwrites tonight's, returning a score of 0-0. Score this:
  //   in-progress > recently completed > closer in time to now > anything else
  // and only overwrite an existing entry if the new candidate scores higher.
  // ── SERIES FIX (2026-07-26) ────────────────────────────────────────────────
  // The map used to keep ONE game per team-pair key, chosen by priority
  // (in-progress > completed > scheduled). That priority was written to stop
  // an UPCOMING game from overwriting tonight's LIVE game — but it has a
  // mirror-image failure for series: when the same two teams played
  // YESTERDAY (completed) and play again TODAY (scheduled), completed beats
  // scheduled and yesterday's final hijacks the key. Observed in prod:
  // a Yankees ML pick for tonight's game rendered "FINAL 3-1" + "Winning"
  // chips from yesterday's game, hours before first pitch.
  //
  // Fix: keep EVERY candidate per key. matchEspn now selects date-aware when
  // the caller supplies a commence time (pick picks its own game day), and
  // falls back to the old priority score when no date is available — so the
  // original Wild@Colorado live-vs-upcoming fix is preserved exactly.
  const map = {};
  const fullNorm = (s = '') => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (const g of allGames) {
    const homeKeys = [g.homeTeamNorm, fullNorm(g.homeTeamFull), fullNorm(g.homeTeamAbbr || '')].filter(Boolean);
    const awayKeys = [g.awayTeamNorm, fullNorm(g.awayTeamFull), fullNorm(g.awayTeamAbbr || '')].filter(Boolean);
    for (const hk of homeKeys) {
      for (const ak of awayKeys) {
        if (!hk || !ak) continue;
        const key = `${hk}|${ak}`;
        if (!map[key]) map[key] = [];
        if (!map[key].includes(g)) map[key].push(g);
      }
    }
  }
  return map;
};

// Resolve any timestamp shape (Firestore Timestamp / Date / ISO string /
// seconds object) to epoch ms, or null. Pick docs carry Firestore Timestamps;
// Odds API games carry ISO strings (commence_time).
const _resolveCommenceMs = (ct) => {
  if (!ct) return null;
  try {
    if (typeof ct?.toDate === 'function') return ct.toDate().getTime();
    if (typeof ct?.seconds === 'number')  return ct.seconds * 1000;
    if (typeof ct?._seconds === 'number') return ct._seconds * 1000;
    if (ct instanceof Date) return ct.getTime();
    const ms = new Date(ct).getTime();
    return isNaN(ms) ? null : ms;
  } catch { return null; }
};

// A candidate ESPN game must start within this window of the pick's own
// commence time to be considered the same game. 12h separates every
// consecutive-day series pairing (even a Sat night -> Sun afternoon turnaround
// is ~18h) while comfortably containing rain delays and doubleheader gaps.
const SERIES_MATCH_WINDOW_MS = 12 * 60 * 60 * 1000;

const matchEspn = (game, espnMap, oddsHomeScore, oddsAwayScore) => {
  // Build the same multi-key set for the Odds API game so we can try EVERY
  // pairing against the ESPN map. The previous code only tried last-word
  // pairs, which silently failed on team-name format differences.
  const fullNorm = (s = '') => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const homeKeys = [
    normTeam(game.homeTeamFull || game.homeTeam || ''),
    fullNorm(game.homeTeamFull || game.homeTeam || ''),
    fullNorm(game.homeTeamAbbr || game.home?.abbreviation || ''),
  ].filter(Boolean);
  const awayKeys = [
    normTeam(game.awayTeamFull || game.awayTeam || ''),
    fullNorm(game.awayTeamFull || game.awayTeam || ''),
    fullNorm(game.awayTeamAbbr || game.away?.abbreviation || ''),
  ].filter(Boolean);

  // Collect ALL candidates for the first matching key — forward orientation
  // first, then reverse. (Map values are arrays since the series fix.)
  let candidates = null;
  let wasReversed = false;
  outer: for (const hk of homeKeys) {
    for (const ak of awayKeys) {
      if (espnMap[`${hk}|${ak}`]?.length) { candidates = espnMap[`${hk}|${ak}`]; break outer; }
    }
  }
  if (!candidates) {
    outer: for (const hk of homeKeys) {
      for (const ak of awayKeys) {
        if (espnMap[`${ak}|${hk}`]?.length) { candidates = espnMap[`${ak}|${hk}`]; wasReversed = true; break outer; }
      }
    }
  }

  let candidate = null;
  if (candidates) {
    // ── DATE-AWARE SELECTION (series fix) ─────────────────────────────────
    // If the caller told us WHEN this game starts (pick.commenceTime or an
    // Odds API game's commence_time), pick the ESPN event closest in time —
    // and refuse anything outside the 12h window: for a pick, no score is
    // strictly better than yesterday's score. Without a caller date, fall
    // back to the original priority (live > recently completed > near
    // scheduled) so the games-board live-vs-upcoming fix is unchanged.
    const targetMs = _resolveCommenceMs(game.commenceTime ?? game.commence_time ?? game.time);
    if (targetMs != null) {
      let best = null, bestDrift = Infinity;
      for (const c of candidates) {
        const cMs = _resolveCommenceMs(c.commenceTime);
        if (cMs == null) continue;
        const drift = Math.abs(cMs - targetMs);
        if (drift <= SERIES_MATCH_WINDOW_MS && drift < bestDrift) { best = c; bestDrift = drift; }
      }
      candidate = best; // may be null: same-name game exists but on a different day
    } else {
      const now = Date.now();
      const prio = (g) => {
        if (g.inProgress) return 1_000_000;
        if (g.completed)  return 500_000 - Math.abs(now - (_resolveCommenceMs(g.commenceTime) ?? 0)) / 1000;
        return 100_000 - Math.abs(now - (_resolveCommenceMs(g.commenceTime) ?? 0)) / 1000;
      };
      for (const c of candidates) if (!candidate || prio(c) > prio(candidate)) candidate = c;
    }
  }

  if (!candidate) {
    // Diagnostic: log misses. Helpful when ESPN's response shape changes.
    if (game.homeTeamFull && game.awayTeamFull) {
      console.warn(`[Torch/ESPN] No match: ${game.awayTeamFull} @ ${game.homeTeamFull} (tried keys: ${[...homeKeys].join(',')}|${[...awayKeys].join(',')})`);
    }
    return null;
  }

  // If we matched via REVERSE lookup, ESPN's home/away are swapped relative to
  // what the Odds API caller expects. We must swap the scores back so that
  // candidate.homeScore = score for the Odds API home team.
  if (wasReversed) {
    candidate = {
      ...candidate,
      homeScore:     candidate.awayScore,
      awayScore:     candidate.homeScore,
      homeTeamFull:  candidate.awayTeamFull,
      awayTeamFull:  candidate.homeTeamFull,
      homeLogo:      candidate.awayLogo,
      awayLogo:      candidate.homeLogo,
      homePitcher:   candidate.awayPitcher,
      awayPitcher:   candidate.homePitcher,
      homeRecord:    candidate.awayRecord,
      awayRecord:    candidate.homeRecord,
      // Playoff series — must also swap home/away status strings + win counts
      // so the row aligned to Odds API home team shows the right "Leads/Trails"
      playoff: candidate.playoff ? {
        round:      candidate.playoff.round,
        homeWins:   candidate.playoff.awayWins,
        awayWins:   candidate.playoff.homeWins,
        homeStatus: candidate.playoff.awayStatus,
        awayStatus: candidate.playoff.homeStatus,
        isOver:     candidate.playoff.isOver,
      } : null,
    };
    console.info(`[Torch/ESPN] Reverse match for ${homeKeys[0]}|${awayKeys[0]} — scores swapped to align with Odds API`);
  }

  const eHome = candidate.homeScore;
  const eAway = candidate.awayScore;

  // Sanity-check: if ESPN and Odds API scores differ by >25 pts, reject.
  // This catches the case where two different games match the same team pair.
  if (
    oddsHomeScore != null && oddsAwayScore != null &&
    eHome != null && eAway != null &&
    (Math.abs(eHome - oddsHomeScore) > 25 || Math.abs(eAway - oddsAwayScore) > 25)
  ) {
    console.warn(
      `[Torch/ESPN] Score mismatch — rejecting ESPN data for ${homeKeys[0]}|${awayKeys[0]}. ` +
      `ESPN: ${eAway}–${eHome}, Odds: ${oddsAwayScore}–${oddsHomeScore}`
    );
    return null;
  }

  return candidate;
};

// ─── NORMALIZE: API game → Torch schema ──────────────────────────────────────
const normalizeGame = (raw, sportKey) => {
  const bm = raw.bookmakers?.find(b => b.key === 'draftkings')
          || raw.bookmakers?.find(b => b.key === 'fanduel')
          || raw.bookmakers?.find(b => b.key === 'betmgm')
          || raw.bookmakers?.find(b => b.key === 'betrivers')
          || raw.bookmakers?.[0];

  if (!bm) return null;

  // Skip outright/futures events that have no home/away teams
  const homeTeam   = raw.home_team;
  const awayTeam   = raw.away_team;
  if (!homeTeam || !awayTeam) return null;

  const h2h     = bm.markets?.find(m => m.key === 'h2h');
  const spreads = bm.markets?.find(m => m.key === 'spreads');
  const totals  = bm.markets?.find(m => m.key === 'totals');
  const homeSpread     = spreads?.outcomes?.find(o => o.name === homeTeam)?.point ?? null;
  const homeSpreadOdds = spreads?.outcomes?.find(o => o.name === homeTeam)?.price ?? null;
  const awaySpreadOdds = spreads?.outcomes?.find(o => o.name === awayTeam)?.price ?? null;
  const total          = totals?.outcomes?.find(o => o.name === 'Over')?.point ?? null;
  const overOdds       = totals?.outcomes?.find(o => o.name === 'Over')?.price  ?? null;
  const underOdds      = totals?.outcomes?.find(o => o.name === 'Under')?.price ?? null;
  const homeML         = h2h?.outcomes?.find(o => o.name === homeTeam)?.price ?? null;
  const awayML         = h2h?.outcomes?.find(o => o.name === awayTeam)?.price ?? null;
  const drawML         = h2h?.outcomes?.find(o => o.name === 'Draw')?.price ?? null;

  const gameTime = new Date(raw.commence_time);
  const now      = Date.now();
  // isLive is intentionally NOT set here from time alone — that produces false positives
  // (games that started but are completed still appear "live").
  // The score status check in fetchLiveGames is the authoritative source.
  const isLive   = false;

  return {
    id:           raw.id,
    sport:        SPORT_LABEL[sportKey] || sportKey,
    sportKey,
    homeTeam:     homeTeam,
    awayTeam:     awayTeam,
    homeTeamAbbr: abbreviate(homeTeam),
    awayTeamAbbr: abbreviate(awayTeam),
    homeTeamFull: homeTeam,
    awayTeamFull: awayTeam,
    time:         raw.commence_time,
    spread:       homeSpread,
    homeSpreadOdds,
    awaySpreadOdds,
    total,
    overOdds,
    underOdds,
    homeML,
    awayML,
    drawML,
    isLive,
    source:       'live',
  };
};

// ─── NORMALIZE: Outright/futures event → list of pickable golfers ───────────
const normalizeOutright = (raw, sportKey) => {
  const bm = raw.bookmakers?.find(b => b.key === 'draftkings')
          || raw.bookmakers?.find(b => b.key === 'fanduel')
          || raw.bookmakers?.find(b => b.key === 'betmgm')
          || raw.bookmakers?.find(b => b.key === 'betrivers')
          || raw.bookmakers?.[0];
  if (!bm) return null;

  const outrights = bm.markets?.find(m => m.key === 'outrights');
  if (!outrights?.outcomes?.length) return null;

  // Sort by odds (favorites first) and take top 20
  const sorted = [...outrights.outcomes]
    .sort((a, b) => {
      const aOdds = a.price > 0 ? a.price : (10000 + a.price);
      const bOdds = b.price > 0 ? b.price : (10000 + b.price);
      return aOdds - bOdds;
    })
    .slice(0, 40);

  return {
    id:           raw.id,
    sport:        SPORT_LABEL[sportKey] || sportKey,
    sportKey,
    homeTeam:     raw.sport_title || 'Tournament Winner',
    awayTeam:     null,
    homeTeamAbbr: 'WINNER',
    awayTeamAbbr: null,
    homeTeamFull: raw.sport_title || 'Tournament Winner',
    awayTeamFull: null,
    time:         raw.commence_time,
    isOutright:   true,
    // Identify the type of outright market
    outrightType: sportKey.endsWith('_winner') ? 'winner'
      : sportKey.endsWith('_top_5') ? 'top_5'
      : sportKey.endsWith('_top_10') ? 'top_10'
      : sportKey.endsWith('_top_20') ? 'top_20'
      : sportKey.endsWith('_make_cut') ? 'make_cut'
      : 'winner',
    // Base tournament key for grouping (strip the market suffix)
    tournamentBase: sportKey.replace(/_(winner|top_5|top_10|top_20|make_cut|matchups)$/, ''),
    outrightOutcomes: sorted.map(o => ({
      name: o.name,
      odds: o.price,
    })),
    // No spread/total/ML for outrights
    spread: null, homeSpreadOdds: null, awaySpreadOdds: null,
    total: null, overOdds: null, underOdds: null,
    homeML: null, awayML: null, drawML: null,
    isLive: false,
    source: 'live',
  };
};
const normalizeProp = (raw, sportKey, gameId) => {
  const bm = raw.bookmakers?.find(b => b.key === 'draftkings')
          || raw.bookmakers?.find(b => b.key === 'fanduel')
          || raw.bookmakers?.[0];
  if (!bm) return [];

  const props = [];
  const seen  = new Set();

  for (const market of bm.markets || []) {
    if (!market.key.startsWith('player_') &&
        !market.key.startsWith('batter_') &&
        !market.key.startsWith('pitcher_')) continue;

    const statLabel = market.key
      .replace(/^(player_|batter_|pitcher_)/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());

    const players = [...new Set(market.outcomes.map(o => o.name))];

    for (const player of players) {
      const key = `${player}-${market.key}`;
      if (seen.has(key)) continue;
      seen.add(key);

      const over  = market.outcomes.find(o => o.name === player && o.description === 'Over');
      const under = market.outcomes.find(o => o.name === player && o.description === 'Under');
      if (!over && !under) continue;

      props.push({
        id:        `prop-${gameId}-${market.key}-${player.replace(/\s+/g, '-')}`,
        sport:     SPORT_LABEL[sportKey] || sportKey,
        gameId,
        player,
        stat:      statLabel,
        line:      over?.point ?? under?.point ?? null,
        overOdds:  over?.price  ?? null,
        underOdds: under?.price ?? null,
        time:      raw.commence_time,
      });
    }
  }
  return props;
};

// ─── FETCH HELPER ─────────────────────────────────────────────────────────────
// URL-level response cache — prevents duplicate API calls within the same load cycle.
// fetchScoreStatusMap and mergeLiveScoresIntoGames hit the same scores endpoints;
// this ensures the second call is a cache hit instead of a second network request.
const _urlCache = {};
const URL_CACHE_TTL = 45_000;

const apiFetch = async (url) => {
  const cached = _urlCache[url];
  if (cached && (Date.now() - cached.ts) < URL_CACHE_TTL) return cached.data;

  const res = await fetch(url);
  const rem = res.headers.get('x-requests-remaining');
  if (rem !== null) _remaining = parseInt(rem, 10);
  if (res.status === 404 || res.status === 422) return null;
  if (res.status === 401) { console.warn('[Torch] 401 Unauthorized — check API key:', url.split('?')[0]); return null; }
  if (res.status === 429) { console.warn('[Torch] 429 Rate limited:', url.split('?')[0]); return null; }
  if (!res.ok) { console.warn(`[Torch] HTTP ${res.status}:`, url.split('?')[0]); return null; }
  _callCount++;
  const data = await res.json();
  _urlCache[url] = { data, ts: Date.now() };
  return data;
};

// ─── FETCH GAMES FOR ONE SPORT ────────────────────────────────────────────────
const fetchSportGames = async (sportKey) => {
  // Outright/winner markets use different API params
  const isOutright = sportKey.endsWith('_winner') || sportKey.endsWith('_top_5') || sportKey.endsWith('_top_10') || sportKey.endsWith('_top_20') || sportKey.endsWith('_make_cut');
  const markets = isOutright ? 'outrights' : 'spreads,totals,h2h';
  // regions=us only. The Odds API bills per region per market, so us,uk,eu was
  // burning 3x quota against the 500/mo cap for a US-facing app. US books cover
  // every market we surface; fetchLiveOdds already uses us alone.
  const url = `${BASE_URL}/sports/${sportKey}/odds/?apiKey=${API_KEY}&regions=us&markets=${markets}&oddsFormat=american&dateFormat=iso`;
  const data = await apiFetch(url);
  if (!data || !Array.isArray(data)) return [];
  const now = Date.now();
  const todayStr = new Date().toLocaleDateString('en-CA');

  if (isOutright) {
    // Outright events: single event with list of golfers/teams and odds
    return data
      .map(g => normalizeOutright(g, sportKey))
      .filter(Boolean);
  }

  return data
    .map(g => normalizeGame(g, sportKey))
    .filter(g => {
      if (!g) return false;
      const msSinceStart = now - new Date(g.time).getTime();
      const gameDay = new Date(g.time).toLocaleDateString('en-CA');
      if (gameDay < todayStr) {
        console.info(`[Torch] Dropping previous-day game: ${g.awayTeamAbbr} @ ${g.homeTeamAbbr} (${gameDay})`);
        return false;
      }
      if (msSinceStart > STALE_GAME_CUTOFF) {
        console.info(`[Torch] Dropping stale game: ${g.awayTeamAbbr} @ ${g.homeTeamAbbr} started ${Math.round(msSinceStart / 3600000)}h ago`);
        return false;
      }
      return true;
    });
};

// ─── FETCH ADDITIONAL MARKETS FOR ONE GAME ──────────────────────────────────
// IMPORTANT: The Odds API has TWO endpoints:
//   1. Sport-level: /sports/{sport}/odds/     — supports h2h, spreads, totals, alts, 1H
//   2. Event-level: /sports/{sport}/events/{id}/odds — supports player props
// Player props are ONLY on the event-level endpoint.
// The event-level endpoint 422s if you send market keys that don't exist for that sport.

let _marketCache = {};
const MARKET_CACHE_TTL = 60 * 1000;

// Fetch from sport-level endpoint, filtered to one game
const fetchSportLevel = async (sportKey, gameId, markets) => {
  const url = `${BASE_URL}/sports/${sportKey}/odds/?apiKey=${API_KEY}&regions=us&markets=${markets}&oddsFormat=american&eventIds=${gameId}`;
  try {
    const res = await fetch(url);
    const rem = res.headers.get('x-requests-remaining');
    if (rem !== null) _remaining = parseInt(rem, 10);
    if (!res.ok) {
      console.warn(`[Torch] ✗ Sport-level ${res.status} for [${markets}]`);
      return null;
    }
    _callCount++;
    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) return null;
    console.info(`[Torch] ✓ Sport-level OK for [${markets}] — ${data[0].bookmakers?.length || 0} books`);
    return data[0];
  } catch (e) {
    console.warn(`[Torch] ✗ Sport-level error for [${markets}]: ${e.message}`);
    return null;
  }
};

// Fetch from event-level endpoint (required for player props)
const fetchEventLevel = async (sportKey, gameId, markets) => {
  const url = `${BASE_URL}/sports/${sportKey}/events/${gameId}/odds?apiKey=${API_KEY}&regions=us&markets=${markets}&oddsFormat=american`;
  try {
    const res = await fetch(url);
    const rem = res.headers.get('x-requests-remaining');
    if (rem !== null) _remaining = parseInt(rem, 10);
    if (!res.ok) {
      let body = '';
      try { body = await res.text(); } catch {}
      console.warn(`[Torch] ✗ Event-level ${res.status} for [${markets}]: ${body.slice(0, 200)}`);
      return null;
    }
    _callCount++;
    const data = await res.json();
    if (!data || !data.bookmakers) return null;
    console.info(`[Torch] ✓ Event-level OK for [${markets}] — ${data.bookmakers.length} books`);
    return data;
  } catch (e) {
    console.warn(`[Torch] ✗ Event-level error for [${markets}]: ${e.message}`);
    return null;
  }
};

// Extract player props from ALL bookmakers in a response
const extractProps = (data, sportKey, gameId) => {
  const bookmakers = data?.bookmakers || [];
  const props = [];
  const seen = new Set();

  // DEBUG: Log the actual structure we're working with
  for (const bm of bookmakers) {
    const marketKeys = (bm.markets || []).map(m => m.key);
    const propMarkets = (bm.markets || []).filter(m =>
      m.key.startsWith('player_') || m.key.startsWith('batter_') || m.key.startsWith('pitcher_'));
    if (propMarkets.length > 0) {
      console.info(`[Torch/DEBUG] ${bm.key} has prop markets: ${propMarkets.map(m => m.key).join(', ')}`);
      for (const m of propMarkets) {
        const sample = m.outcomes?.slice(0, 3) || [];
        console.info(`[Torch/DEBUG]   ${m.key}: ${m.outcomes?.length || 0} outcomes. Sample:`, JSON.stringify(sample));
      }
    } else {
      console.info(`[Torch/DEBUG] ${bm.key} markets: ${marketKeys.join(', ')} (no prop markets)`);
    }
  }

  for (const bm of bookmakers) {
    for (const market of bm.markets || []) {
      if (!market.key.startsWith('player_') &&
          !market.key.startsWith('batter_') &&
          !market.key.startsWith('pitcher_')) continue;
      const statLabel = market.key
        .replace(/^(player_|batter_|pitcher_)/, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      
      // Try to extract using description field (standard format)
      const players = [...new Set(market.outcomes.map(o => o.description || o.name))];
      for (const player of players) {
        const key = `${player}-${market.key}`;
        if (seen.has(key)) continue;
        seen.add(key);

        // Standard format: name=player, description=Over/Under
        let over  = market.outcomes.find(o => o.name === player && o.description === 'Over');
        let under = market.outcomes.find(o => o.name === player && o.description === 'Under');
        
        // Alt format: description=player, name=Over/Under
        if (!over && !under) {
          over  = market.outcomes.find(o => o.description === player && o.name === 'Over');
          under = market.outcomes.find(o => o.description === player && o.name === 'Under');
        }

        if (!over && !under) continue;
        props.push({
          id: `prop-${gameId}-${market.key}-${player.replace(/\s+/g, '-')}`,
          sport: SPORT_LABEL[sportKey] || sportKey,
          gameId, player, stat: statLabel,
          line: over?.point ?? under?.point ?? null,
          overOdds: over?.price ?? null,
          underOdds: under?.price ?? null,
          time: data.commence_time,
        });
      }
    }
  }
  return props;
};

// Sport-specific prop market keys (CRITICAL: only send keys valid for the sport)
const getPropKeys = (sportKey) => {
  if (sportKey?.includes('basketball'))
    return ['player_points', 'player_rebounds', 'player_assists', 'player_threes'];
  if (sportKey?.includes('americanfootball'))
    return ['player_pass_yds', 'player_pass_tds', 'player_rush_yds', 'player_reception_yds', 'player_anytime_td'];
  if (sportKey?.includes('baseball'))
    return ['batter_home_runs', 'batter_hits', 'batter_total_bases', 'pitcher_strikeouts'];
  if (sportKey?.includes('hockey'))
    return ['player_goals', 'player_shots_on_goal'];
  if (sportKey?.includes('soccer'))
    return ['player_goals', 'player_shots_on_target'];
  if (sportKey?.includes('mma'))
    return ['fighter_method_of_victory'];
  if (sportKey?.includes('aussierules'))
    return ['player_disposals', 'player_goals'];
  if (sportKey?.includes('rugbyleague'))
    return ['player_tryscorer'];
  return [];
};

// Single source of truth for "does this sport offer player props?" — derived
// directly from getPropKeys so the two can never drift. Exported for the client
// (TorchApp) to gate the "Player Props" entry point on game rows without
// duplicating the sport list. Keep prop-coverage logic ONLY in getPropKeys.
export const sportHasProps = (sportKey) => getPropKeys(sportKey).length > 0;

export const fetchGameMarkets = async (sportKey, gameId, homeTeam, awayTeam) => {
  if (!API_KEY) return null;
  const cached = _marketCache[gameId];
  if (cached && Date.now() - cached.ts < MARKET_CACHE_TTL) return cached.data;

  let mainLines = {};
  let altSpreads = [];
  let altTotals = [];
  let firstHalf = {};
  let teamTotals = [];
  let playerProps = [];

  const pickBm = (bms) =>
    bms?.find(b => b.key === 'draftkings')
    || bms?.find(b => b.key === 'fanduel')
    || bms?.find(b => b.key === 'betmgm')
    || bms?.[0] || null;

  console.info(`[Torch] ── Game detail: ${gameId} (${sportKey}) ──`);

  // ── GOLF OUTRIGHT DETAIL — fetch finishing position props ──
  const isOutrightEvent = sportKey.endsWith('_winner');
  if (isOutrightEvent) {
    console.info(`[Torch] Outright event — fetching finish position markets`);
    let golfProps = [];
    // Fetch top finish & make cut markets from event-level endpoint
    const golfMarkets = ['outrights','outrights_top_5','outrights_top_10','outrights_top_20','outrights_make_cut'];
    for (const mkt of golfMarkets) {
      try {
        const data = await fetchEventLevel(sportKey, gameId, mkt);
        if (data?.bookmakers?.length > 0) {
          const bk = pickBm(data.bookmakers);
          const market = bk?.markets?.find(m => m.key === mkt);
          if (market?.outcomes?.length > 0) {
            const label = mkt === 'outrights' ? 'Tournament Winner'
              : mkt === 'outrights_top_5' ? 'Top 5 Finish'
              : mkt === 'outrights_top_10' ? 'Top 10 Finish'
              : mkt === 'outrights_top_20' ? 'Top 20 Finish'
              : mkt === 'outrights_make_cut' ? 'Make the Cut'
              : mkt;
            const sorted = [...market.outcomes]
              .sort((a, b) => {
                const aO = a.price > 0 ? a.price : (10000 + a.price);
                const bO = b.price > 0 ? b.price : (10000 + b.price);
                return aO - bO;
              })
              .slice(0, mkt === 'outrights' ? 40 : 30);
            golfProps.push({
              marketKey: mkt,
              label,
              outcomes: sorted.map(o => ({ name: o.name, odds: o.price })),
            });
            console.info(`[Torch] ✓ ${mkt}: ${sorted.length} outcomes`);
          }
        }
      } catch (e) {
        console.warn(`[Torch] ✗ ${mkt}: ${e.message}`);
      }
    }
    const result = { mainLines: {}, altSpreads: [], altTotals: [], firstHalf: {}, teamTotals: [], playerProps: [], golfProps };
    _marketCache[gameId] = { data: result, ts: Date.now() };
    console.info(`[Torch] ── Done (outright): ${golfProps.length} golf prop markets ──`);
    return result;
  }

  // ── 1. Main lines (sport-level — always works) ──
  const mainData = await fetchSportLevel(sportKey, gameId, 'h2h,spreads,totals');
  const bm = pickBm(mainData?.bookmakers);
  if (bm) {
    const f = (key) => bm.markets?.find(m => m.key === key);
    mainLines = {
      homeSpread:     f('spreads')?.outcomes?.find(o => o.name === homeTeam)?.point ?? null,
      homeSpreadOdds: f('spreads')?.outcomes?.find(o => o.name === homeTeam)?.price ?? null,
      awaySpreadOdds: f('spreads')?.outcomes?.find(o => o.name === awayTeam)?.price ?? null,
      total:          f('totals')?.outcomes?.find(o => o.name === 'Over')?.point ?? null,
      overOdds:       f('totals')?.outcomes?.find(o => o.name === 'Over')?.price ?? null,
      underOdds:      f('totals')?.outcomes?.find(o => o.name === 'Under')?.price ?? null,
      homeML:         f('h2h')?.outcomes?.find(o => o.name === homeTeam)?.price ?? null,
      awayML:         f('h2h')?.outcomes?.find(o => o.name === awayTeam)?.price ?? null,
      drawML:         f('h2h')?.outcomes?.find(o => o.name === 'Draw')?.price ?? null,
    };
  }

  // ── 2. Alt lines (sport-level) ──
  const altData = await fetchSportLevel(sportKey, gameId, 'alternate_spreads,alternate_totals');
  const abm = pickBm(altData?.bookmakers);
  if (abm) {
    const am = abm.markets || [];
    const asr = am.find(m => m.key === 'alternate_spreads');
    altSpreads = asr ? asr.outcomes.map(o => ({
      team: o.name, point: o.point, odds: o.price,
      side: o.name === homeTeam ? 'home' : 'away',
    })) : [];
    const atr = am.find(m => m.key === 'alternate_totals');
    altTotals = atr ? atr.outcomes.map(o => ({
      side: o.name === 'Over' ? 'over' : 'under',
      point: o.point, odds: o.price,
    })) : [];
  }

  // ── 3. First half (sport-level) ──
  const fhData = await fetchSportLevel(sportKey, gameId, 'h2h_1st_half,spreads_1st_half,totals_1st_half');
  const fbm = pickBm(fhData?.bookmakers);
  if (fbm) {
    const fm = fbm.markets || [];
    const f = (key) => fm.find(m => m.key === key);
    firstHalf = {
      homeML:         f('h2h_1st_half')?.outcomes?.find(o => o.name === homeTeam)?.price ?? null,
      awayML:         f('h2h_1st_half')?.outcomes?.find(o => o.name === awayTeam)?.price ?? null,
      drawML:         f('h2h_1st_half')?.outcomes?.find(o => o.name === 'Draw')?.price ?? null,
      homeSpread:     f('spreads_1st_half')?.outcomes?.find(o => o.name === homeTeam)?.point ?? null,
      homeSpreadOdds: f('spreads_1st_half')?.outcomes?.find(o => o.name === homeTeam)?.price ?? null,
      awaySpreadOdds: f('spreads_1st_half')?.outcomes?.find(o => o.name === awayTeam)?.price ?? null,
      total:          f('totals_1st_half')?.outcomes?.find(o => o.name === 'Over')?.point ?? null,
      overOdds:       f('totals_1st_half')?.outcomes?.find(o => o.name === 'Over')?.price ?? null,
      underOdds:      f('totals_1st_half')?.outcomes?.find(o => o.name === 'Under')?.price ?? null,
    };
  } else {
    firstHalf = {};
  }

  // ── 4. Player props (EVENT-LEVEL — the ONLY endpoint that serves props) ──
  const propKeys = getPropKeys(sportKey);
  if (propKeys.length > 0) {
    // Try all sport-specific props in one call
    console.info(`[Torch] Fetching props via event endpoint: ${propKeys.join(',')}`);
    const propData = await fetchEventLevel(sportKey, gameId, propKeys.join(','));
    if (propData?.bookmakers?.length > 0) {
      playerProps = extractProps(propData, sportKey, gameId);
    }

    // If combined call failed or returned 0, try each market individually
    if (playerProps.length === 0) {
      console.info(`[Torch] Trying props individually...`);
      for (const mkt of propKeys) {
        const single = await fetchEventLevel(sportKey, gameId, mkt);
        if (single?.bookmakers?.length > 0) {
          const p = extractProps(single, sportKey, gameId);
          if (p.length > 0) {
            console.info(`[Torch] ✓ ${mkt}: ${p.length} props`);
            playerProps.push(...p);
          } else {
            console.info(`[Torch] ✗ ${mkt}: 0 props in response`);
          }
        }
      }
    }
    console.info(`[Torch] Player props total: ${playerProps.length}`);
  }

  const result = { mainLines, altSpreads, altTotals, firstHalf, teamTotals, playerProps };
  _marketCache[gameId] = { data: result, ts: Date.now() };
  console.info(`[Torch] ── Done: ${altSpreads.length} alts, ${playerProps.length} props ──`);
  return result;
};

// ─── FETCH PROPS FOR ONE GAME (legacy — used by fetchPlayerProps bulk loader) ─
const fetchGameProps = async (sportKey, gameId) => {
  const propKeys = getPropKeys(sportKey);
  if (propKeys.length === 0) return [];
  const url = `${BASE_URL}/sports/${sportKey}/events/${gameId}/odds?apiKey=${API_KEY}&regions=us&markets=${propKeys.join(',')}&oddsFormat=american`;
  const data = await apiFetch(url);
  if (!data) return [];
  return normalizeProp(data, sportKey, gameId);
};

const DESIRED_SPORT_KEYS = new Set(Object.keys(SPORT_LABEL));

const SPORT_KEY_BLACKLIST = new Set([
  // Futures/winner markets — no game-level odds, just outright winners.
  // NOTE: this set is currently unreferenced; the single source of truth for
  // what gets fetched is getActiveSportKeys() below, which deliberately omits
  // ALL futures/outright keys per the no-futures product principle documented
  // there. Kept as an explicit record of futures-style keys we never surface.
  'golf_masters_tournament_winner',
  'golf_masters_tournament_top_5',
  'golf_masters_tournament_top_10',
  'golf_masters_tournament_top_20',
  'golf_masters_tournament_make_cut',
  'golf_pga_championship_winner',
  'golf_the_open_championship_winner',
  'golf_us_open_winner',
  'americanfootball_nfl_super_bowl_winner',
  'americanfootball_ncaaf_championship_winner',
  'basketball_ncaab_championship_winner',
  'basketball_nba_championship_winner',
  'baseball_mlb_world_series_winner',
  'soccer_fifa_world_cup_winner',
  'soccer_usa_mls_winner',
  'icehockey_nhl_championship_winner',
  // Politics (not a sport)
  'politics_us_presidential_election_winner',
]);

const FALLBACK_SPORTS = [
  'basketball_nba',
  'basketball_ncaab',
  'basketball_wncaab',
  'icehockey_nhl',
  'baseball_mlb',
  'americanfootball_nfl',
  'soccer_usa_mls',
  'soccer_epl',
  'soccer_uefa_champs_league',
  'soccer_spain_la_liga',
  'soccer_italy_serie_a',
  'soccer_germany_bundesliga',
  'soccer_france_ligue_one',
  'mma_mixed_martial_arts',
  'boxing_boxing',
  'aussierules_afl',
  'rugbyleague_nrl',
  'basketball_euroleague',
  'americanfootball_cfl',
];

// ─── DYNAMIC SPORT DISCOVERY ─────────────────────────────────────────────────
const getActiveSportKeys = async () => {
  // Core sports only — hardcoded list, no API call needed.
  // The old code fetched 130+ sport keys causing 15-20s load times.
  // Each sport key = 1 API call to fetch games. Keep this list tight.
  return [
    'basketball_nba',
    'basketball_wnba',
    'icehockey_nhl',
    'baseball_mlb',
    'americanfootball_nfl',
    'americanfootball_ncaaf',
    'basketball_ncaab',
    'basketball_wncaab',
    'mma_mixed_martial_arts',
    'boxing_boxing',
    'soccer_usa_mls',
    'soccer_epl',
    'soccer_uefa_champs_league',
    'soccer_spain_la_liga',
    'soccer_italy_serie_a',
    'soccer_germany_bundesliga',
    'soccer_france_ligue_one',
    // FIFA World Cup 2026 — happening now (Jun 11 – Jul 19). Fully mapped in
    // SPORT_LABEL ('World Cup'), ESPN_SPORT_PATH ('soccer/fifa.world') and the
    // client SPORT_FILTER_MAP/SOCCER_KEYS, but was never in this request list,
    // so WC fixtures never reached the Games tab for prediction. (The summer
    // off-season leagues above legitimately have no fixtures in June.)
    'soccer_fifa_world_cup',
    // ── PRODUCT PRINCIPLE: NO FUTURES / SEASON-LONG OUTRIGHTS ──────────────
    // Torch is a 24/7/365 prediction app where users compete inside a league
    // window. Every pick must be able to SETTLE inside that window so the core
    // loop (predict → settle → standings move) actually closes. Futures and
    // season-long outrights (Super Bowl winner, division winner, season win
    // totals, tournament-winner / top-N / make-cut fields) resolve long after a
    // typical league ends, stranding points in wagers that never grade. So we
    // deliberately DO NOT request any outright/futures market here.
    //
    // What stays: discrete, near-term, head-to-head markets only. Golf is
    // included ONLY via *_matchups (golfer-vs-golfer H2H that settles per round)
    // — never the tournament-winner/top-N/make-cut outright keys.
    // Do not re-add *_winner, *_top_5/10/20, *_make_cut, or a bare tournament
    // key (which returns outright winner odds) without revisiting this principle.
    'golf_masters_tournament_matchups',
    'tennis_atp',
    'tennis_wta',
  ];
};

// ─── SCORES CROSS-VALIDATION ──────────────────────────────────────────────────
const fetchScoreStatusMap = async (sportKeys) => {
  const map = {};
  // Fetch all sport scores in parallel (was sequential with 100ms delays)
  const results = await Promise.allSettled(
    sportKeys.map(async (sportKey) => {
      try {
        const url = `${BASE_URL}/sports/${sportKey}/scores/?apiKey=${API_KEY}&daysFrom=2`;
        const data = await apiFetch(url);
        if (!Array.isArray(data)) return [];
        return data.map(g => {
          const hasScores = g.scores?.length >= 2;
          const parseScore = (val) => { if (val == null) return null; const n = parseFloat(val); return isNaN(n) ? null : n; };
          const homeScore = hasScores ? parseScore(g.scores.find(s => s.name === g.home_team)?.score) : null;
          const awayScore = hasScores ? parseScore(g.scores.find(s => s.name === g.away_team)?.score) : null;
          return {
            id: g.id,
            completed:  g.completed ?? false,
            inProgress: !g.completed && hasScores,
            homeScore,
            awayScore,
            period: g.period ?? null,
            clock:  g.clock  ?? null,
          };
        });
      } catch (e) {
        console.warn(`[Torch] Score status failed for ${sportKey}:`, e.message);
        return [];
      }
    })
  );
  for (const r of results) {
    if (r.status !== 'fulfilled') continue;
    for (const g of r.value) {
      map[g.id] = g;
    }
  }
  return map;
};

// ─── MAIN: fetchLiveGames ─────────────────────────────────────────────────────
export const fetchLiveGames = async () => {
  if (!API_KEY) {
    console.info('[Torch] No API key — using mock data.');
    return getMockGames();
  }

  if (_gamesCache && (Date.now() - _gamesCacheTime) < CACHE_TTL) {
    console.info('[Torch] Returning cached games.');
    return _gamesCache;
  }

  try {
    const now = Date.now();
    const activeSports = await getActiveSportKeys();

    const allGames = [];
    // Fetch all sports in one parallel batch (core list is ~20 sports max)
    const BATCH_SIZE = 20;
    for (let i = 0; i < activeSports.length; i += BATCH_SIZE) {
      const batch = activeSports.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map(async (sportKey) => {
          try {
            const games = await fetchSportGames(sportKey);
            if (games.length) console.info(`[Torch] ${sportKey}: ${games.length} games`);
            return games;
          } catch (e) {
            console.warn(`[Torch] ${sportKey} failed:`, e.message);
            return [];
          }
        })
      );
      for (const r of results) {
        if (r.status === 'fulfilled') allGames.push(...r.value);
      }
    }

    if (allGames.length === 0) {
      console.warn('[Torch] No games from API — likely a connection or API issue.');
      throw new Error('No games returned from API');
    }

    const seen = new Set();
    const idDeduped = allGames.filter(g => {
      if (seen.has(g.id)) return false;
      seen.add(g.id);
      return true;
    });

    // Secondary dedup: same two teams on the same calendar day = same real-world game.
    // The Odds API can return the same matchup under multiple sport keys
    // (e.g. baseball_mlb + baseball_mlb_preseason) with different event IDs.
    // Keep the first occurrence (which is from the higher-priority sport key).
    const seenMatchup = new Set();
    const deduplicated = idDeduped.filter(g => {
      const day = new Date(g.time).toLocaleDateString('en-CA');
      const matchupKey = [g.homeTeam, g.awayTeam].sort().join('|') + '|' + day;
      if (seenMatchup.has(matchupKey)) {
        console.info(`[Torch] Dropping duplicate matchup: ${g.awayTeamAbbr} @ ${g.homeTeamAbbr} (${g.sportKey})`);
        return false;
      }
      seenMatchup.add(matchupKey);
      return true;
    });

    const startedGames = deduplicated.filter(g => new Date(g.time).getTime() <= now);
    let scoreMap = {};
    if (startedGames.length > 0) {
      const sportsToCheck = [...new Set(startedGames.map(g => g.sportKey).filter(Boolean))];
      scoreMap = await fetchScoreStatusMap(sportsToCheck);
    }

    const games = deduplicated
      .map(g => {
        const sc = scoreMap[g.id];
        const msSinceStart = now - new Date(g.time).getTime();

        if (!sc) {
          if (msSinceStart > 1 * 60 * 60 * 1000) {
            console.info(`[Torch] Dropping started game absent from scores: ${g.awayTeamAbbr} @ ${g.homeTeamAbbr}`);
            return null;
          }
          return { ...g, isLive: false }; // no score data — assume not live
        }
        if (sc.completed) return null;

        // TIME-BASED SAFETY: if the game started more than 6 hours ago,
        // treat it as completed regardless of what the API says.
        // The Odds API sometimes returns completed:false for finished games
        // (especially from the previous day), causing stale "LIVE" tags.
        const MAX_GAME_DURATION = 6 * 60 * 60 * 1000; // 6 hours
        if (msSinceStart > MAX_GAME_DURATION) {
          console.info(`[Torch] Dropping stale "live" game (${Math.round(msSinceStart/3600000)}h old): ${g.awayTeamAbbr} @ ${g.homeTeamAbbr}`);
          return null;
        }

        if (sc.inProgress) {
          return { ...g, isLive: true, completed: false };
        }
        return { ...g, isLive: false }; // confirmed not in progress
      })
      .filter(Boolean);

    if (games.length === 0) {
      console.warn('[Torch] All games filtered out.');
      throw new Error('No games available after filtering');
    }

    games.sort((a, b) => {
      if (a.isLive && !b.isLive) return -1;
      if (!a.isLive && b.isLive) return 1;
      return new Date(a.time) - new Date(b.time);
    });

    _gamesCache     = games;
    _gamesCacheTime = now;

    const sportCounts = games.reduce((acc, g) => { acc[g.sport] = (acc[g.sport] || 0) + 1; return acc; }, {});
    const liveCount   = games.filter(g => g.isLive).length;
    console.info(`[Torch] ${games.length} games (${liveCount} live):`, sportCounts, `| Quota: ${_remaining} left`);
    return games;

  } catch (err) {
    console.error('[Torch] fetchLiveGames error:', err.message);
    // Serve the real cached games if we have them — better than an error screen
    // when we just had valid data seconds ago. But NEVER fall back to mock games
    // on connection failure: users could make picks against fictional matchups.
    if (_gamesCache && _gamesCache.length > 0) {
      console.info('[Torch] Serving cached games after fetch error.');
      return _gamesCache;
    }
    throw err;
  }
};

// ─── MAIN: fetchPlayerProps ───────────────────────────────────────────────────
export const fetchPlayerProps = async (games = []) => {
  if (!API_KEY) return getMockPropBets();

  if (_propsCache && (Date.now() - _propsCacheTime) < CACHE_TTL) {
    return _propsCache;
  }

  const targetGames = games
    .filter(g => g.isLive || (new Date(g.commenceTime || g.time) - Date.now()) < 4 * 3600 * 1000)
    .slice(0, 8);

  if (targetGames.length === 0) return getMockPropBets();

  try {
    const results = await Promise.allSettled(
      targetGames.map(g => fetchGameProps(g.sportKey, g.id))
    );

    const props = results
      .filter(r => r.status === 'fulfilled')
      .flatMap(r => r.value);

    if (props.length === 0) {
      console.info('[Torch] Props unavailable on current API plan — using mock props.');
      return getMockPropBets();
    }

    _propsCache     = props;
    _propsCacheTime = Date.now();
    console.info(`[Torch] ${props.length} player props loaded.`);
    return props;

  } catch (err) {
    console.error('[Torch] fetchPlayerProps error:', err.message);
    return getMockPropBets();
  }
};

// ─── LIVE IN-PLAY ODDS ────────────────────────────────────────────────────────
export const fetchLiveOdds = async (liveGames = []) => {
  if (!API_KEY || liveGames.length === 0) return {};

  // Group live games by sport
  const bySport = {};
  for (const g of liveGames) {
    if (!g.sportKey) continue;
    if (!bySport[g.sportKey]) bySport[g.sportKey] = [];
    bySport[g.sportKey].push(g.id);
  }

  const result = {};

  const results = await Promise.allSettled(
    Object.entries(bySport).map(async ([sportKey, gameIds]) => {
      const cached = _liveOddsCache[sportKey];
      if (cached && Date.now() - cached.ts < LIVE_ODDS_TTL) {
        return cached.data;
      }

      try {
        // Use sport-level /odds/ endpoint with eventIds filter (proven to work)
        // instead of /odds-live which returns 404 on this plan
        const url = `${BASE_URL}/sports/${sportKey}/odds/?apiKey=${API_KEY}&regions=us&markets=h2h,spreads,totals&oddsFormat=american&eventIds=${gameIds.join(',')}`;
        const res = await fetch(url);
        const rem = res.headers.get('x-requests-remaining');
        if (rem !== null) _remaining = parseInt(rem, 10);

        if (!res.ok) {
          _liveOddsCache[sportKey] = { data: {}, ts: Date.now() };
          return {};
        }
        _callCount++;
        const data = await res.json();
        if (!Array.isArray(data)) return {};

        const sportData = {};
        for (const event of data) {
          const bm = event.bookmakers?.find(b => b.key === 'draftkings')
                  || event.bookmakers?.find(b => b.key === 'fanduel')
                  || event.bookmakers?.find(b => b.key === 'betmgm')
                  || event.bookmakers?.[0];
          if (!bm) continue;

          const h2h     = bm.markets?.find(m => m.key === 'h2h');
          const spreads = bm.markets?.find(m => m.key === 'spreads');
          const totals  = bm.markets?.find(m => m.key === 'totals');

          const homeTeam = event.home_team;
          const awayTeam = event.away_team;

          sportData[event.id] = {
            spread:         spreads?.outcomes?.find(o => o.name === homeTeam)?.point ?? null,
            homeSpreadOdds: spreads?.outcomes?.find(o => o.name === homeTeam)?.price ?? null,
            awaySpreadOdds: spreads?.outcomes?.find(o => o.name === awayTeam)?.price ?? null,
            total:          totals?.outcomes?.find(o => o.name === 'Over')?.point ?? null,
            overOdds:       totals?.outcomes?.find(o => o.name === 'Over')?.price ?? null,
            underOdds:      totals?.outcomes?.find(o => o.name === 'Under')?.price ?? null,
            homeML:         h2h?.outcomes?.find(o => o.name === homeTeam)?.price ?? null,
            awayML:         h2h?.outcomes?.find(o => o.name === awayTeam)?.price ?? null,
            drawML:         h2h?.outcomes?.find(o => o.name === 'Draw')?.price ?? null,
            homeScore:      event.scores?.find(s => s.name === homeTeam)?.score ?? null,
            awayScore:      event.scores?.find(s => s.name === awayTeam)?.score ?? null,
            oddsUpdatedAt:  Date.now(),
            isLiveOdds:     true,
          };
        }

        _liveOddsCache[sportKey] = { data: sportData, ts: Date.now() };
        return sportData;

      } catch (e) {
        console.warn(`[Torch] Live odds failed for ${sportKey}:`, e.message);
        return {};
      }
    })
  );

  for (const r of results) {
    if (r.status === 'fulfilled') Object.assign(result, r.value);
  }

  return result;
};

// ─── FETCH COMPLETED SCORES (for settlement) ─────────────────────────────────
// FIX: null-safe parseScore ensures null API values stay null (not coerced to 0)
// which would cause over picks to wrongly lose on 0+0 combined scores.
export const fetchScores = async (sportKey, daysFrom = 3) => {
  if (!API_KEY) return [];
  const url = `${BASE_URL}/sports/${sportKey}/scores/?apiKey=${API_KEY}&daysFrom=${daysFrom}`;
  const data = await apiFetch(url);
  if (!data || !Array.isArray(data)) return [];
  const parseScore = (val) => {
    if (val == null) return null;
    const n = parseFloat(val);
    return isNaN(n) ? null : n;
  };

  return data
    .filter(g => g.completed && g.scores && g.scores.length >= 2)
    .map(g => {
      const homeScore = parseScore(g.scores.find(s => s.name === g.home_team)?.score);
      const awayScore = parseScore(g.scores.find(s => s.name === g.away_team)?.score);
      return {
        id:        g.id,
        sportKey,
        homeTeam:  g.home_team,
        awayTeam:  g.away_team,
        homeScore,
        awayScore,
        completed: true,
        // CLIENT-DATE-GUARDS (fix 1.4, 2026-09-28): settlement.js's date-drift
        // guards and 90-minute minimum age both read commence_time off this
        // result. Without it every client guard skipped silently, so an aliased
        // gameId (the 5/22 series case) graded a pick from the wrong game.
        commence_time: g.commence_time || null,
      };
    })
    .filter(g => g.homeScore != null && g.awayScore != null);
};

// ─── LIVE SCORES for pending picks ───────────────────────────────────────────
// FIX C1: Replaced parseInt()?? '0' || 0 with null-safe parseScore throughout.
// FIX C2: Increased daysFrom from 2 → 3 to match settlement's first-pass lookback.
// FIX C3: ESPN null scores no longer fall back to 0 (parseEspnScore in fetchEspnSport).
//
// 2026-07 LIVE-SCORE REDESIGN — ESPN-FIRST:
// Live scores + clock now come from ESPN (free, no quota, real-time). The Odds
// API /scores/ endpoint is called ONLY as a fallback for games ESPN couldn't
// match — not on every poll for every sport. Previously this fetched Odds API
// scores for every sport each poll and then OVERRODE them with ESPN, which
// (a) burned rate-limited Odds API quota on the hot path as usage grew, and
// (b) added a slow paid call to every 30s live refresh. Odds API is now used
// for ODDS, not scores. Result map stays keyed by the pick's Odds API gameId so
// the consumer (which matches by p.gameId) is unchanged.
export const fetchLiveScoresForPicks = async (picks) => {
  if (!picks?.length) return {};

  // Flatten to scorable entries, walking parlay legs. Each entry keeps the
  // gameId (result key) + the team names/sport ESPN needs to match directly.
  const entries = []; // { gameId, sportKey, homeTeamFull, awayTeamFull, commenceTime }
  const seen = new Set();
  const addEntry = (sportKey, gameId, homeTeamFull, awayTeamFull, commenceTime) => {
    if (!sportKey || !gameId || seen.has(gameId)) return;
    seen.add(gameId);
    // commenceTime rides along so matchEspn can do DATE-AWARE selection —
    // without it, a series (same teams, consecutive days) resolves to
    // whichever game the priority heuristic prefers, and yesterday's
    // completed final hijacks today's pick (prod bug 2026-07-26).
    entries.push({ gameId, sportKey, homeTeamFull: homeTeamFull || '', awayTeamFull: awayTeamFull || '', commenceTime: commenceTime || null });
  };
  for (const p of picks) {
    if (p.isParlay && Array.isArray(p.legs)) {
      for (const leg of p.legs) addEntry(leg.sportKey, leg.gameId, leg.homeTeamFull, leg.awayTeamFull, leg.commenceTime);
    } else {
      addEntry(p.sportKey, p.gameId, p.homeTeamFull, p.awayTeamFull, p.commenceTime);
    }
  }
  if (!entries.length) return {};

  const sportKeys = [...new Set(entries.map(e => e.sportKey))];

  // 1) ESPN first — one cheap fetch per sport (cached ~20s), no quota.
  const espnMap = await getEspnScores(sportKeys);

  const result = {};
  const missed = []; // entries ESPN couldn't resolve → Odds API fallback

  for (const e of entries) {
    // Match this pick's game directly against the ESPN map by team names.
    // Pass null odds-scores: the >25pt cross-check is inert without them (we
    // have no Odds API score to compare yet — that's the whole point).
    const espn = (e.homeTeamFull && e.awayTeamFull)
      ? matchEspn({ homeTeamFull: e.homeTeamFull, awayTeamFull: e.awayTeamFull, commenceTime: e.commenceTime }, espnMap, null, null)
      : null;
    // BELT: never attach a COMPLETED result to a pick whose game hasn't
    // started yet (30min grace for feed clock skew). If the date-aware match
    // somehow still lands on a finished game while this pick's kickoff is in
    // the future, showing nothing is correct and settlement stays untouched.
    // Use the shared module-level resolver rather than a local copy — the
    // inline version that used to live here omitted the `_seconds` shape and
    // could silently disarm the belt for picks read straight off the wire.
    const pickMs = _resolveCommenceMs(e.commenceTime);
    const futureGame = pickMs != null && pickMs > Date.now() + 30 * 60 * 1000;
    if (espn && espn.completed && futureGame) {
      missed.push(e);
      continue;
    }
    if (espn && (espn.homeScore != null || espn.awayScore != null || espn.completed || espn.inProgress)) {
      result[e.gameId] = {
        homeTeam:  e.homeTeamFull,
        awayTeam:  e.awayTeamFull,
        homeScore: espn.homeScore ?? null,
        awayScore: espn.awayScore ?? null,
        completed: espn.completed ?? false,
        period:    espn.period ?? null,
        clock:     espn.clock  ?? null,
      };
    } else {
      missed.push(e);
    }
  }

  // 2) Odds API fallback — ONLY for sports that still have unresolved games.
  // Most polls resolve entirely via ESPN and never reach this block.
  if (missed.length && API_KEY) {
    const parseScore = (val) => {
      if (val == null) return null;
      const n = parseFloat(val);
      return isNaN(n) ? null : n;
    };
    const missedSports = [...new Set(missed.map(e => e.sportKey))];
    const missedGameIds = new Set(missed.map(e => e.gameId));
    // ── BELT PARITY (2026-07-26, round 7) ─────────────────────────────────────
    // The ESPN loop above pushes a pick into `missed` precisely BECAUSE the
    // belt rejected a completed score for a game that hasn't kicked off. This
    // fallback then refilled result[g.id] for those same picks with no belt
    // and no date check — handing the rejected score straight back through the
    // other door. The Odds API is the source that has historically ALIASED
    // gameIds across same-matchup series games (see the 5/22/26 note in
    // functions/index.js), so it is the last feed that should go unguarded.
    // Carry each missed entry's own commence time forward and apply the same
    // two rules the ESPN path uses.
    const missedCommenceMs = new Map();
    for (const e of missed) missedCommenceMs.set(e.gameId, _resolveCommenceMs(e.commenceTime));
    const ODDS_FALLBACK_DRIFT_MS = 12 * 60 * 60 * 1000;
    await Promise.all(
      missedSports.map(async (sportKey) => {
        try {
          const url = `${BASE_URL}/sports/${sportKey}/scores/?apiKey=${API_KEY}&daysFrom=3`;
          const data = await apiFetch(url);
          if (!Array.isArray(data)) return;
          for (const g of data) {
            if (!missedGameIds.has(g.id)) continue; // only fill the gaps
            if (!g.scores || g.scores.length < 2) continue;
            const homeScore = parseScore(g.scores.find(s => s.name === g.home_team)?.score);
            const awayScore = parseScore(g.scores.find(s => s.name === g.away_team)?.score);
            if (homeScore == null || awayScore == null) continue;
            const entryMs = missedCommenceMs.get(g.id) ?? null;
            // BELT: don't attach a completed score to a game still in the future.
            if ((g.completed ?? false) && entryMs != null &&
                entryMs > Date.now() + 30 * 60 * 1000) {
              console.warn(`[Torch/Odds] Suppressing completed score for ${g.away_team} @ ${g.home_team} — pick kickoff is still in the future (likely aliased gameId).`);
              continue;
            }
            // DRIFT: reject a payload whose own start time is a different day
            // from the pick's. Matches the 12h window used by matchEspn and by
            // the client settlement result-drift guards.
            if (entryMs != null && g.commence_time) {
              const gMs = new Date(g.commence_time).getTime();
              if (!isNaN(gMs) && Math.abs(gMs - entryMs) > ODDS_FALLBACK_DRIFT_MS) {
                console.warn(`[Torch/Odds] Rejecting score for ${g.away_team} @ ${g.home_team} — ${Math.round(Math.abs(gMs - entryMs) / 3600000)}h date drift vs pick commenceTime.`);
                continue;
              }
            }
            result[g.id] = {
              homeTeam:  g.home_team,
              awayTeam:  g.away_team,
              homeScore,
              awayScore,
              completed: g.completed ?? false,
              period:    null,
              clock:     null,
            };
          }
        } catch { /* silently skip — ESPN already covered the rest */ }
      })
    );
  }

  return result;
};

// ─── MERGE LIVE SCORES into games array ──────────────────────────────────────
export const mergeLiveScoresIntoGames = async (games) => {
  if (!games?.length) return games;

  const sportKeys = [...new Set(games.filter(g => g.sportKey).map(g => g.sportKey))];
  const espnMap = await getEspnScores(sportKeys);

  // Direct league-wide team records — fetched in parallel with everything
  // else. Gives us a record for every team in every league we care about,
  // independent of whether the scoreboard endpoint matched the game. This
  // is the bulletproof fallback: if espn match fails OR the scoreboard
  // didn't expose records for that game, we look up by team name here.
  //
  // We derive ESPN paths from BOTH the sportKey (e.g. "basketball_nba")
  // AND the sport LABEL (e.g. "NBA"). Some games have futures-style sport
  // keys like "basketball_nba_championship_winner" that aren't in
  // ESPN_SPORT_PATH, but their sport label maps cleanly to a known path.
  // Without this dual-source resolution, those games silently fail.
  const SPORT_LABEL_PATH = {
    'NBA':       'basketball/nba',
    'WNBA':      'basketball/wnba',
    'NCAAB':     'basketball/mens-college-basketball',
    'NFL':       'football/nfl',
    'NCAAF':     'football/college-football',
    'NHL':       'hockey/nhl',
    'MLB':       'baseball/mlb',
    'PLL':       'lacrosse/pll',
    'MLS':       'soccer/usa.1',
    'EPL':       'soccer/eng.1',
    'La Liga':   'soccer/esp.1',
    'Serie A':   'soccer/ita.1',
    'Bundesliga':'soccer/ger.1',
    'Ligue 1':   'soccer/fra.1',
    'UCL':       'soccer/uefa.champions',
    'AFL':       'australian-football/afl',
    'NRL':       'rugby-league/nrl',
  };
  const espnPaths = [...new Set([
    ...sportKeys.map(k => ESPN_SPORT_PATH[k]),
    ...games.map(g => SPORT_LABEL_PATH[g.sport]),
  ].filter(Boolean))];
  const recordsByPath = {};
  await Promise.all(
    espnPaths.map(async (path) => {
      recordsByPath[path] = await fetchTeamRecords(path);
    })
  );

  const oddsScoreMap = {};
  if (API_KEY) {
    await Promise.all(
      sportKeys.map(async (sportKey) => {
        try {
          const url = `${BASE_URL}/sports/${sportKey}/scores/?apiKey=${API_KEY}&daysFrom=2`;
          const data = await apiFetch(url);
          if (!Array.isArray(data)) return;
          const parseScore = (val) => { if (val == null) return null; const n = parseFloat(val); return isNaN(n) ? null : n; };
          for (const g of data) {
            if (!g.scores || g.scores.length < 2) continue;
            const homeScore = parseScore(g.scores.find(s => s.name === g.home_team)?.score);
            const awayScore = parseScore(g.scores.find(s => s.name === g.away_team)?.score);
            oddsScoreMap[g.id] = {
              homeScore,
              awayScore,
              completed:  g.completed ?? false,
              inProgress: !g.completed && g.scores?.length >= 2,
            };
          }
        } catch {}
      })
    );
  }

  // Identify which sports are currently in their postseason. We do this by
  // scanning all games before the per-game merge — if ANY game in a sport
  // has playoff data attached (via espn match), that sport is in playoffs.
  // For sports flagged as in-playoffs, we suppress the regular-season
  // record fallback: if a specific playoff game lacks series data, we
  // leave its record line blank rather than show the misleading season W-L.
  const sportsInPlayoffs = new Set();
  for (const g of games) {
    const odds = oddsScoreMap[g.id];
    const knownH = odds?.homeScore ?? g.home?.score ?? null;
    const knownA = odds?.awayScore ?? g.away?.score ?? null;
    const espn = matchEspn(g, espnMap, knownH, knownA);
    if (espn?.playoff) sportsInPlayoffs.add(g.sport);
  }

  return games.map(g => {
    const odds = oddsScoreMap[g.id];
    const knownHomeScore = odds?.homeScore ?? g.home?.score ?? null;
    const knownAwayScore = odds?.awayScore ?? g.away?.score ?? null;
    const espn = matchEspn(g, espnMap, knownHomeScore, knownAwayScore);

    if (!odds && !espn) return g;

    const homeScore = espn?.homeScore ?? odds?.homeScore ?? g.home?.score;
    const awayScore = espn?.awayScore ?? odds?.awayScore ?? g.away?.score;

    const completed  = odds?.completed  ?? false;
    const inProgress = espn?.inProgress ?? odds?.inProgress ?? g.isLive ?? false;

    const period = (!completed && espn?.inProgress && espn?.period) ? espn.period : null;
    const clock  = (!completed && espn?.inProgress && espn?.clock)  ? espn.clock  : null;

    console.debug(
      `[Torch/ESPN] ${g.awayTeam} @ ${g.homeTeam}` +
      ` | ESPN matched: ${espn ? '✓' : '✗'}` +
      ` | inProgress: ${espn?.inProgress ?? '—'}` +
      ` | period: ${period ?? '—'} clock: ${clock ?? '—'}` +
      ` | score: ${awayScore}–${homeScore}`
    );

    // Standings-fallback record lookup — but ONLY when this sport isn't
    // currently in its postseason. A playoff game without series data
    // shows nothing rather than the misleading regular-season record.
    const sportInPlayoffs = sportsInPlayoffs.has(g.sport);
    const sportKeyPath = ESPN_SPORT_PATH[g.sportKey];
    const sportLabelPath = SPORT_LABEL_PATH[g.sport];
    const recordsForLeague =
      (sportKeyPath && recordsByPath[sportKeyPath]) ||
      (sportLabelPath && recordsByPath[sportLabelPath]) ||
      null;
    const fallbackHomeRecord = sportInPlayoffs ? null : lookupTeamRecord(recordsForLeague, g.home?.full || g.homeTeam, g.home?.abbreviation || g.homeTeamAbbr);
    const fallbackAwayRecord = sportInPlayoffs ? null : lookupTeamRecord(recordsForLeague, g.away?.full || g.awayTeam, g.away?.abbreviation || g.awayTeamAbbr);

    return {
      ...g,
      // TIME-BASED SAFETY: don't mark as live if game started 6+ hours ago
      // Use commenceTime (raw ISO) since adaptGame converts time to display format
      isLive:    (inProgress || g.isLive) && (Date.now() - new Date(g.commenceTime || g.time).getTime() < 6 * 3600000),
      completed: completed || (Date.now() - new Date(g.commenceTime || g.time).getTime() >= 6 * 3600000),
      period,
      clock,
      homeLogo: espn?.homeLogo || g.homeLogo || null,
      awayLogo: espn?.awayLogo || g.awayLogo || null,
      home: { ...g.home, score: homeScore },
      away: { ...g.away, score: awayScore },
      homePitcher: espn?.homePitcher || g.homePitcher || null,
      awayPitcher: espn?.awayPitcher || g.awayPitcher || null,
      homeRecord:  espn?.homeRecord  || g.homeRecord  || fallbackHomeRecord || null,
      awayRecord:  espn?.awayRecord  || g.awayRecord  || fallbackAwayRecord || null,
      playoff:     espn?.playoff     || g.playoff     || null,
      // Rich in-game situation (bases/count/outs for MLB, down & distance for
      // football). Only surface it while the game is actually in progress; a
      // stale liveState on a finished game would render a phantom diamond.
      liveState:   (inProgress && espn?.liveState) ? espn.liveState : null,
    };
  });
};

// ─── MOCK DATA ────────────────────────────────────────────────────────────────
const h = (hrs)  => new Date(Date.now() + hrs * 3600000).toISOString();
const d = (days) => h(days * 24);

export const getMockGames = () => [
  { id:'nba-1', sport:'NBA', homeTeam:'OKC', awayTeam:'IND', homeTeamFull:'Oklahoma City Thunder', awayTeamFull:'Indiana Pacers',         time:h(-1.2), spread:-6.5, total:218.5, isLive:true,  homeScore:67, awayScore:58, period:'3',    clock:'4:22',  homeML:-260, awayML:215,  homeSpreadOdds:-110, awaySpreadOdds:-110, overOdds:-110, underOdds:-110 },
  { id:'nba-2', sport:'NBA', homeTeam:'LAL', awayTeam:'GSW', homeTeamFull:'Los Angeles Lakers',    awayTeamFull:'Golden State Warriors',  time:h(2),    spread:2.5,  total:224.0, isLive:false, homeML:115,  awayML:-135, homeSpreadOdds:-108, awaySpreadOdds:-112, overOdds:-110, underOdds:-110 },
  { id:'nba-3', sport:'NBA', homeTeam:'DEN', awayTeam:'PHX', homeTeamFull:'Denver Nuggets',        awayTeamFull:'Phoenix Suns',           time:h(4.5),  spread:-4.0, total:231.5, isLive:false, homeML:-180, awayML:155,  homeSpreadOdds:-110, awaySpreadOdds:-110, overOdds:-115, underOdds:-105 },
  { id:'nba-4', sport:'NBA', homeTeam:'NYK', awayTeam:'MIL', homeTeamFull:'New York Knicks',       awayTeamFull:'Milwaukee Bucks',        time:h(7),    spread:1.5,  total:215.0, isLive:false, homeML:105,  awayML:-125, homeSpreadOdds:-110, awaySpreadOdds:-110, overOdds:-110, underOdds:-110 },
  { id:'nba-5', sport:'NBA', homeTeam:'OKC', awayTeam:'MIN', homeTeamFull:'Oklahoma City Thunder', awayTeamFull:'Minnesota Timberwolves', time:d(1),    spread:-3.5, total:219.5, isLive:false, homeML:-165, awayML:140,  homeSpreadOdds:-112, awaySpreadOdds:-108, overOdds:-110, underOdds:-110 },
  { id:'nhl-1', sport:'NHL', homeTeam:'TOR', awayTeam:'MTL', homeTeamFull:'Toronto Maple Leafs',   awayTeamFull:'Montreal Canadiens',     time:h(-0.8), spread:-1.5, total:6.0,   isLive:true,  homeScore:2,  awayScore:1, period:'2', clock:'11:04', homeML:-145, awayML:125  },
  { id:'nhl-2', sport:'NHL', homeTeam:'COL', awayTeam:'VGK', homeTeamFull:'Colorado Avalanche',    awayTeamFull:'Vegas Golden Knights',   time:h(3.5),  spread:-1.5, total:6.5,   isLive:false, homeML:-120, awayML:100  },
  { id:'ncaab-1', sport:'NCAAB', homeTeam:'Duke',    awayTeam:'NC St',  homeTeamFull:'Duke Blue Devils',     awayTeamFull:'NC State Wolfpack',  time:h(2),  spread:-12.5, total:148.5, isLive:false, homeML:-600, awayML:450 },
  { id:'ncaab-2', sport:'NCAAB', homeTeam:'Kansas',  awayTeam:'Baylor', homeTeamFull:'Kansas Jayhawks',      awayTeamFull:'Baylor Bears',       time:h(4),  spread:-5.5,  total:144.0, isLive:false, homeML:-230, awayML:190 },
  { id:'mlb-1', sport:'MLB', homeTeam:'LAD', awayTeam:'SF',  homeTeamFull:'Los Angeles Dodgers',   awayTeamFull:'San Francisco Giants',   time:h(-1.5), spread:-1.5, total:8.5, isLive:true,  homeScore:3, awayScore:1, period:'Bot 6th', clock:'', homeML:-165, awayML:140 },
  { id:'mlb-2', sport:'MLB', homeTeam:'NYY', awayTeam:'BOS', homeTeamFull:'New York Yankees',      awayTeamFull:'Boston Red Sox',         time:h(2.5),  spread:-1.5, total:9.0, isLive:false, homeML:-140, awayML:120 },
  { id:'epl-1', sport:'EPL', homeTeam:'ARS', awayTeam:'LIV', homeTeamFull:'Arsenal',               awayTeamFull:'Liverpool',              time:h(1),    spread:null, total:2.5, isLive:false, homeML:200,  awayML:135, drawML:240 },
  { id:'mls-1', sport:'MLS', homeTeam:'ATL', awayTeam:'MIA', homeTeamFull:'Atlanta United FC',     awayTeamFull:'Inter Miami CF',         time:h(1),    spread:null, total:2.5, isLive:false, homeML:140, awayML:180, drawML:230 },
];

export const getMockPropBets = () => [
  { id:'prop-1',  sport:'NBA', player:'Jayson Tatum',      stat:'Points',     line:28.5, time:h(-1.2), overOdds:-115, underOdds:-105 },
  { id:'prop-2',  sport:'NBA', player:'LeBron James',      stat:'Points',     line:25.5, time:h(2),    overOdds:-110, underOdds:-110 },
  { id:'prop-3',  sport:'NBA', player:'Stephen Curry',     stat:'3-Pointers', line:4.5,  time:h(2),    overOdds:120,  underOdds:-140 },
  { id:'prop-4',  sport:'NHL', player:'Auston Matthews',   stat:'Goals',      line:0.5,  time:h(-0.8), overOdds:140,  underOdds:-165 },
  { id:'prop-5',  sport:'MLB', player:'Shohei Ohtani',     stat:'Total Bases',line:1.5,  time:h(-1.5), overOdds:-120, underOdds:100  },
];

export const getFeaturedParlays = () => [
  { id:'feat-1', label:'SAME GAME PARLAY', title:'Celtics Cover + Tatum Over 28.5 Pts', odds:'+285', sport:'NBA', picks:2, trending:true, timesPlaced:847 },
  { id:'feat-2', label:'PARLAY OF THE DAY', title:'Chiefs ML + Mahomes Over 284.5 Yds', odds:'+410', sport:'NFL', picks:2, trending:true, timesPlaced:1243 },
];

export const getOddsBoosts = () => [
  { id:'boost-1', sport:'NBA', description:'Celtics to win by 10+', originalOdds:'+180', boostedOdds:'+240' },
  { id:'boost-2', sport:'NHL', description:'Matthews scores + Leafs win', originalOdds:'+220', boostedOdds:'+300' },
];
